'use client';

/**
 * Auto Zonas Altas -multi-page PT/EN/DE renderer.
 *
 * Ported from the Claude Design handoff (.design-handoff/). Design system is
 * inline-styled (the source is 100% inline style); one responsive renderer
 * (clamp() + a small injected stylesheet) replaces the separate desktop/mobile
 * artboards. Pages are selected by `routePath`; language by path prefix.
 * See docs/auto-zonas-altas-implementation.md.
 */

import { useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import {
  EditableImage,
  EditableList,
  EditableText,
  sanitizePhoneHref,
  sanitizeWhatsAppHref,
  sanitizeMapUrl,
  trackEvent,
  useEditMode,
  type ImageValue,
  type SiteRendererProps,
} from '@locusly/client-runtime';
import type {
  AboutCredentialItem,
  AutozonasaltasContent,
  PriceTierIncludeItem,
  PricingTierCardItem,
  PricingRow,
  ReviewItem,
  ServiceFaqItem,
  ServiceItem,
  ServiceStepItem,
  TrustStripItem,
} from './content.schema';

/* ─── Tokens ──────────────────────────────────────────────────────── */
const aza = {
  ink: '#0C1F24', teal900: '#0B3038', teal800: '#13434E', teal700: '#1B5462',
  teal500: '#3A7C8B', teal100: '#D7E6E9', teal50: '#EDF3F4',
  bone: '#F4EFE6', paper: '#FAF7F1', rule: '#E4DED1', ruleStrong: '#C8C0AE',
  smoke: '#67615A', graphite: '#2A2622',
  signal: '#D9531E', signalSoft: '#FBEADD',
  whatsapp: '#25D366', star: '#E8A317', ok: '#1F7A4F', danger: '#B5341F',
  white: '#FFFFFF',
};
const FONT_DISPLAY = '"Archivo","Helvetica Neue",Helvetica,Arial,sans-serif';
const FONT_BODY = '"Manrope","Helvetica Neue",Helvetica,Arial,sans-serif';
const FONT_MONO = '"JetBrains Mono",ui-monospace,Menlo,monospace';
const T = {
  h1: { fontFamily: FONT_DISPLAY, fontWeight: 800, lineHeight: 1.03, letterSpacing: '-0.02em', fontSize: 'clamp(34px,5vw,60px)' },
  h2: { fontFamily: FONT_DISPLAY, fontWeight: 700, lineHeight: 1.08, letterSpacing: '-0.015em', fontSize: 'clamp(26px,3.4vw,36px)' },
  h3: { fontFamily: FONT_DISPLAY, fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.01em', fontSize: 'clamp(20px,2.4vw,24px)' },
  h4: { fontFamily: FONT_DISPLAY, fontWeight: 700, lineHeight: 1.2, fontSize: 18 },
  eyebrow: { fontFamily: FONT_BODY, fontWeight: 700, fontSize: 12, lineHeight: 1, letterSpacing: '0.14em', textTransform: 'uppercase' as const },
  lead: { fontFamily: FONT_BODY, fontWeight: 500, fontSize: 'clamp(16px,1.4vw,19px)', lineHeight: 1.5 },
  body: { fontFamily: FONT_BODY, fontWeight: 400, fontSize: 16, lineHeight: 1.6 },
  small: { fontFamily: FONT_BODY, fontWeight: 500, fontSize: 13, lineHeight: 1.45 },
  micro: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: 11, lineHeight: 1.3, letterSpacing: '0.02em' },
  mono: { fontFamily: FONT_MONO, fontWeight: 500, fontSize: 13, letterSpacing: '0.01em' },
};
const SHADOW = {
  card: '0 1px 0 rgba(12,31,36,0.04), 0 1px 2px rgba(12,31,36,0.06)',
  lift: '0 18px 40px -16px rgba(12,31,36,0.30), 0 2px 6px rgba(12,31,36,0.06)',
};

/* ─── Icons (monoline, no emoji) ──────────────────────────────────── */
type IconProps = { size?: number };
const stroke = (d: ReactNode, sz = 16, fill = false): ReactNode => (
  <svg viewBox="0 0 24 24" width={sz} height={sz} fill={fill ? 'currentColor' : 'none'}
    stroke={fill ? undefined : 'currentColor'} strokeWidth="1.7" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true">{d}</svg>
);
const Icon = {
  whatsapp: ({ size = 16 }: IconProps) => stroke(<path d="M12 2.5C6.75 2.5 2.5 6.75 2.5 12c0 1.7.45 3.3 1.24 4.7L2.5 21.5l4.95-1.2A9.5 9.5 0 0 0 12 21.5c5.25 0 9.5-4.25 9.5-9.5S17.25 2.5 12 2.5Zm5.55 13.4c-.24.66-1.38 1.26-1.92 1.34-.49.08-1.12.11-1.81-.11-.42-.13-.96-.31-1.65-.61-2.91-1.26-4.8-4.18-4.95-4.37-.14-.19-1.18-1.57-1.18-3 0-1.43.75-2.13 1.02-2.42.27-.29.59-.36.79-.36.2 0 .39 0 .56.01.18.01.42-.07.66.5.25.6.83 2.06.9 2.21.07.15.12.32.02.5-.09.18-.14.29-.27.45-.13.16-.28.36-.4.48-.13.13-.27.27-.12.53.16.27.7 1.16 1.51 1.88 1.04.93 1.93 1.22 2.2 1.36.27.13.43.11.59-.07.16-.18.68-.79.86-1.06.18-.27.36-.22.6-.13.25.09 1.59.75 1.86.88.27.13.45.2.52.31.07.11.07.64-.17 1.3Z" />, size, true),
  phone: ({ size = 16 }: IconProps) => stroke(<path d="M5 4h3l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v3a2 2 0 0 1-2 2A15 15 0 0 1 3 6a2 2 0 0 1 2-2Z" />, size),
  pin: ({ size = 16 }: IconProps) => stroke(<><path d="M12 22s7-7.2 7-12a7 7 0 0 0-14 0c0 4.8 7 12 7 12Z" /><circle cx="12" cy="10" r="2.5" /></>, size),
  star: ({ size = 14 }: IconProps) => stroke(<path d="m12 17.3-6.18 3.7 1.64-7.04L2 9.24l7.19-.62L12 2l2.81 6.62L22 9.24l-5.46 4.72L18.18 21Z" />, size, true),
  check: ({ size = 16 }: IconProps) => stroke(<path d="m4 12 5 5 11-11" />, size),
  arrow: ({ size = 16 }: IconProps) => stroke(<path d="M5 12h14M13 5l7 7-7 7" />, size),
  menu: ({ size = 20 }: IconProps) => stroke(<path d="M4 7h16M4 12h16M4 17h16" />, size),
  plus: ({ size = 16 }: IconProps) => stroke(<path d="M12 5v14M5 12h14" />, size),
  minus: ({ size = 16 }: IconProps) => stroke(<path d="M5 12h14" />, size),
  clock: ({ size = 16 }: IconProps) => stroke(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>, size),
  shield: ({ size = 18 }: IconProps) => stroke(<><path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6l-8-3Z" /><path d="m9 12 2 2 4-4" /></>, size),
  wrench: ({ size = 18 }: IconProps) => stroke(<path d="M14.7 6.3a4 4 0 0 0 5 5L21 12.5a8 8 0 0 1-10.5 10.5L9.3 21.7l-7-7L3.5 13.5A8 8 0 0 1 14 3l1 1Z" />, size),
  spark: ({ size = 18 }: IconProps) => stroke(<path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />, size),
  car: ({ size = 18 }: IconProps) => stroke(<><path d="M3 13.5 5 8h14l2 5.5M3 13.5h18v5H3z" /><circle cx="7.5" cy="17.5" r="1.5" /><circle cx="16.5" cy="17.5" r="1.5" /></>, size),
  drop: ({ size = 18 }: IconProps) => stroke(<path d="M12 3s6 6.5 6 11a6 6 0 1 1-12 0c0-4.5 6-11 6-11Z" />, size),
  snowflake: ({ size = 18 }: IconProps) => stroke(<path d="M12 2v20M2 12h20M4.9 4.9l14.2 14.2M19.1 4.9 4.9 19.1" />, size),
  circle: ({ size = 18 }: IconProps) => stroke(<><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /></>, size),
  hammer: ({ size = 18 }: IconProps) => stroke(<path d="m15 4 5 5-3 3-5-5Zm-3 3L3 16l3 3 9-9Z" />, size),
  brush: ({ size = 18 }: IconProps) => stroke(<><path d="M14 4h6v6L9 21l-5-5Z" /><path d="m9 16 4-4" /></>, size),
  burst: ({ size = 18 }: IconProps) => stroke(<path d="M12 2 14 8l5-2-2 5 6 1-6 1 2 5-5-2-2 6-2-6-5 2 2-5-6-1 6-1-2-5 5 2Z" />, size),
  doc: ({ size = 18 }: IconProps) => stroke(<><path d="M6 3h9l4 4v14H6z" /><path d="M15 3v5h4M9 13h7M9 17h5" /></>, size),
  obd: ({ size = 18 }: IconProps) => stroke(<><rect x="3" y="5" width="18" height="11" rx="2" /><path d="M7 19h10M12 16v3M8 12h8" /></>, size),
  gauge: ({ size = 18 }: IconProps) => stroke(<><path d="M3 14a9 9 0 1 1 18 0" /><path d="m12 14 4-4" /></>, size),
  globe: ({ size = 16 }: IconProps) => stroke(<><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></>, size),
};
const iconByName = (name: string, size = 20): ReactNode => {
  const fn = (Icon as Record<string, (p: IconProps) => ReactNode>)[name];
  return fn ? fn({ size }) : <Icon.wrench size={size} />;
};

/* ─── Parsing helpers (pipe/newline-encoded content) ──────────────── */
const lines = (s?: string): string[] =>
  (s ?? '').split('\n').map((l) => l.trim()).filter(Boolean);
const parts = (s: string): string[] => s.split('|').map((p) => p.trim());
const normalizeTrustStrip = (value?: TrustStripItem[] | string): TrustStripItem[] =>
  Array.isArray(value)
    ? value
    : lines(value).map((line) => {
      const [stat = '', label = ''] = parts(line);
      return { value: stat, label };
    });
const normalizeCredentials = (value?: AboutCredentialItem[] | string): AboutCredentialItem[] =>
  Array.isArray(value)
    ? value
    : lines(value).map((line) => {
      const [title = '', description = ''] = parts(line);
      return { title, description };
    });
type TextLineItem = { text: string };
type FaqEntryItem = { question: string; answer: string };
type DamageItem = { name: string; price: string; ready: string };
const normalizeTextItems = (value?: TextLineItem[] | string): TextLineItem[] =>
  Array.isArray(value)
    ? value
    : lines(value).map((text) => ({ text }));
const normalizeFaqItems = (value?: FaqEntryItem[] | string): FaqEntryItem[] =>
  Array.isArray(value)
    ? value
    : lines(value).map((line) => {
      const [question = '', answer = ''] = parts(line);
      return { question, answer };
    });
const normalizeDamageItems = (value?: DamageItem[] | string): DamageItem[] =>
  Array.isArray(value)
    ? value
    : lines(value).map((line) => {
      const [name = '', price = '', ready = ''] = parts(line);
      return { name, price, ready };
    });
const normalizePriceTierIncludes = (value?: PriceTierIncludeItem[]): PriceTierIncludeItem[] =>
  value?.length
    ? value
    : [
      { label: 'Peças e mão-de-obra incluídas' },
      { label: 'Orçamento por escrito' },
      { label: 'Garantia 24 meses' },
    ];
const normalizePricingTierCards = (
  value: PricingTierCardItem[] | undefined,
  fallbackIncludes: PriceTierIncludeItem[],
  fallbackCtaLabel: string,
  fallbackBadge: string,
  fallbackFromLabel: string,
): PricingTierCardItem[] => {
  if (value?.length) return value;
  return [0, 1, 2].map((index) => ({
    badge: index === 1 ? fallbackBadge : '',
    fromLabel: fallbackFromLabel,
    ctaLabel: fallbackCtaLabel,
    include1: fallbackIncludes[0]?.label ?? '',
    include2: fallbackIncludes[1]?.label ?? '',
    include3: fallbackIncludes[2]?.label ?? '',
  }));
};
const normalizeServiceSteps = (value?: ServiceStepItem[] | string): ServiceStepItem[] =>
  Array.isArray(value)
    ? value
    : lines(value).map((line) => {
      const [title = '', meta = '', body = ''] = parts(line);
      return { title, meta, body };
    });
const normalizeServiceFaqs = (value?: ServiceFaqItem[] | string): ServiceFaqItem[] =>
  Array.isArray(value)
    ? value
    : lines(value).map((line) => {
      const [question = '', answer = ''] = parts(line);
      return { question, answer };
    });
const normalizeDiffCard = (value?: string): { title: string; body: string } => {
  const [title = '', body = ''] = parts(value ?? '');
  return { title, body };
};

/* ─── i18n labels ─────────────────────────────────────────────────── */
type Lang = 'pt' | 'en' | 'de';
const L = {
  pt: { wa: 'Marcar pelo WhatsApp', call: 'Telefonar', pricing: 'Pedir orçamento', book: 'Marcar pelo WhatsApp', dir: 'Como chegar', services: 'Serviços', reviews: 'Avaliações', allServices: 'Ver os 12 serviços', find: 'Encontrar a oficina' },
  en: { wa: 'Message us on WhatsApp', call: 'Call', pricing: 'Request a quote', book: 'Book on WhatsApp', dir: 'Directions', services: 'Services', reviews: 'Reviews', allServices: 'See all services', find: 'Find us' },
  de: { wa: 'Per WhatsApp anschreiben', call: 'Anrufen', pricing: 'Angebot anfragen', book: 'WhatsApp Termin', dir: 'Anfahrt', services: 'Leistungen', reviews: 'Bewertungen', allServices: 'Alle Leistungen', find: 'Standort' },
};
const servicesPathFor = (lang: Lang): string => lang === 'en' ? '/en/services' : '/servicos';
const contactPathFor = (lang: Lang): string => lang === 'en' ? '/en/contact' : lang === 'de' ? '/de/kontakt' : '/contactos';
const quoteCtaPathFor = (lang: Lang): string => lang === 'pt' ? '/marcacao' : contactPathFor(lang);
const servicePathFor = (lang: Lang, slug: string): string => {
  if (lang !== 'en') return `/servicos/${slug}`;
  if (slug === 'bate-chapa') return '/en/services/body-shop';
  if (slug === 'mecanica') return '/en/services/mechanic';
  if (slug === 'reparacao-pos-acidente') return '/en/services/rental-car-damage';
  return `/en/services/${slug}`;
};
const quotePathFor = (lang: Lang, slug: string): string =>
  lang === 'en'
    ? `/en/contact?service=${slug}`
    : lang === 'de'
      ? `/de/kontakt?service=${slug}`
      : `/marcacao?servico=${slug}`;
const navFor = (lang: Lang, c: AutozonasaltasContent): Array<[string, string, string]> =>
  lang === 'en'
    ? [['/en/', c.nav.homeEn, 'nav.homeEn'], ['/en/services/rental-car-damage', c.nav.rentalEn, 'nav.rentalEn'], ['/en/services/body-shop', c.nav.bodyShopEn, 'nav.bodyShopEn'], [quoteCtaPathFor('en'), c.nav.pricingEn, 'nav.pricingEn']]
    : lang === 'de'
      ? [['/de/', c.nav.startDe, 'nav.startDe'], [quoteCtaPathFor('de'), c.nav.pricingDe, 'nav.pricingDe']]
      : [['/', c.nav.homePt, 'nav.homePt'], ['/servicos', c.nav.servicesPt, 'nav.servicesPt'], [quoteCtaPathFor('pt'), c.nav.pricingPt, 'nav.pricingPt'], ['/sobre', c.nav.aboutPt, 'nav.aboutPt'], ['/testemunhos', c.nav.reviewsPt, 'nav.reviewsPt'], ['/contactos', c.nav.contactsPt, 'nav.contactsPt']];

/* ─── CTA helpers + analytics ─────────────────────────────────────── */
function waUrl(whatsapp: string, text: string): string | undefined {
  const href = sanitizeWhatsAppHref(whatsapp);
  if (!href) return undefined;
  return `${href}?text=${encodeURIComponent(text)}`;
}
function onCta(type: 'whatsapp_click' | 'phone_click' | 'cta_click' | 'directions_click', label: string) {
  return () => trackEvent(type, { label });
}

/* ─── Primitives ──────────────────────────────────────────────────── */
function Eyebrow({ children, color = aza.teal700, style }: { children: ReactNode; color?: string; style?: CSSProperties }) {
  return <div style={{ ...T.eyebrow, color, ...style }}>{children}</div>;
}
function Badge({ children, tone = 'default', style }: { children: ReactNode; tone?: 'default' | 'signal' | 'success' | 'bone' | 'dark'; style?: CSSProperties }) {
  const pal = {
    default: { bg: aza.teal50, fg: aza.teal800, b: aza.teal100 },
    signal: { bg: aza.signalSoft, fg: aza.signal, b: '#F2C9AC' },
    success: { bg: '#E6F1EA', fg: aza.ok, b: '#C0DCC9' },
    bone: { bg: aza.bone, fg: aza.ink, b: aza.rule },
    dark: { bg: 'rgba(255,255,255,.10)', fg: aza.paper, b: 'rgba(255,255,255,.18)' },
  }[tone];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: pal.bg, color: pal.fg, border: `1px solid ${pal.b}`, padding: '4px 10px', borderRadius: 999, ...T.micro, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', ...style }}>{children}</span>
  );
}
function Stars({ value, count, color = aza.star, size = 14, dark = false, valueLabel, countLabel, valuePath, countPath }: { value: number; count?: number; color?: string; size?: number; dark?: boolean; valueLabel?: string; countLabel?: string; valuePath?: string; countPath?: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: dark ? aza.paper : aza.ink }}>
      <span style={{ display: 'inline-flex', gap: 1, color }}>{[0, 1, 2, 3, 4].map((i) => <Icon.star key={i} size={size} />)}</span>
      {valuePath
        ? <EditableText path={valuePath} as="span" style={{ ...T.small, fontWeight: 700, color: 'inherit' }}>{valueLabel ?? value.toFixed(1)}</EditableText>
        : <span style={{ ...T.small, fontWeight: 700, color: 'inherit' }}>{valueLabel ?? value.toFixed(1)}</span>}
      {count !== undefined && (countPath
        ? <EditableText path={countPath} as="span" style={{ ...T.small, color: dark ? 'rgba(255,255,255,.6)' : aza.smoke }}>{countLabel ?? `(${count})`}</EditableText>
        : <span style={{ ...T.small, color: dark ? 'rgba(255,255,255,.6)' : aza.smoke }}>{countLabel ?? `(${count})`}</span>)}
    </span>
  );
}
function BtnWA({ label, href, size = 'md', full = false }: { label: ReactNode; href?: string; size?: 'sm' | 'md' | 'lg'; full?: boolean }) {
  const pad = size === 'lg' ? '14px 22px' : size === 'sm' ? '8px 14px' : '12px 18px';
  const fs = size === 'lg' ? 16 : size === 'sm' ? 13 : 15;
  return (
    <a href={href || '#'} target={href ? '_top' : undefined} onClick={onCta('whatsapp_click', 'whatsapp')}
      style={{ background: aza.whatsapp, color: '#06321B', padding: pad, borderRadius: 6, ...T.body, fontWeight: 700, fontSize: fs, display: full ? 'flex' : 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9, width: full ? '100%' : 'auto', boxShadow: '0 1px 0 rgba(0,0,0,.12), inset 0 -2px 0 rgba(0,0,0,.10)' }}>
      <Icon.whatsapp size={size === 'lg' ? 20 : 18} />{label}
    </a>
  );
}
function BtnGhost({ label, href, icon, dark = false, size = 'md', full = false, onClick }: { label: ReactNode; href?: string; icon?: ReactNode; dark?: boolean; size?: 'sm' | 'md' | 'lg'; full?: boolean; onClick?: () => void }) {
  const pad = size === 'lg' ? '13px 20px' : size === 'sm' ? '7px 12px' : '11px 16px';
  return (
    <a href={href || '#'} target={href ? '_top' : undefined} onClick={onClick}
      style={{ background: 'transparent', color: dark ? aza.paper : aza.ink, border: `1px solid ${dark ? 'rgba(255,255,255,0.30)' : aza.ruleStrong}`, padding: pad, borderRadius: 6, ...T.body, fontWeight: 600, fontSize: size === 'lg' ? 15 : 14, display: full ? 'flex' : 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: full ? '100%' : 'auto' }}>
      {icon}{label}
    </a>
  );
}
function BtnLink({ label, href }: { label: ReactNode; href?: string }) {
  return <a href={href || '#'} target={href ? '_top' : undefined} style={{ color: aza.teal700, textDecoration: 'underline', textUnderlineOffset: 3, ...T.body, fontWeight: 600 }}>{label} →</a>;
}
function Section({ children, dark = false, bone = false, signal = false, style }: { children: ReactNode; dark?: boolean; bone?: boolean; signal?: boolean; style?: CSSProperties }) {
  const bg = dark ? aza.teal900 : signal ? aza.signal : bone ? aza.bone : aza.paper;
  return (
    <section style={{ background: bg, color: dark || signal ? aza.paper : aza.ink }}>
      <div className="aza-inner" style={{ padding: '72px 56px', ...style }}>{children}</div>
    </section>
  );
}

function EditAnchor({ path, value, multiline = false }: { path: string; value?: string; multiline?: boolean; showInEditMode?: boolean }) {
  if (!value) return null;
  return (
    <EditableText
      path={path}
      as="div"
      multiline={multiline}
      style={{ display: 'none' }}
    >
      {value}
    </EditableText>
  );
}

/* ─── Photo slot (editable image; striped placeholder until uploaded) */
function PhotoSlot({ path, value, label, ratio = '16/10', dark = false, minHeight }: { path: string; value?: ImageValue; label: string; ratio?: string; dark?: boolean; minHeight?: number }) {
  const bg = dark ? aza.teal900 : '#E9E2D2';
  const stripeC = dark ? 'rgba(255,255,255,0.05)' : 'rgba(12,31,36,0.05)';
  const ink = dark ? 'rgba(255,255,255,0.78)' : 'rgba(12,31,36,0.6)';
  return (
    <div style={{ position: 'relative', width: '100%', aspectRatio: ratio, minHeight, background: `repeating-linear-gradient(135deg, ${bg} 0 14px, ${stripeC} 14px 28px)`, borderRadius: 10, display: 'flex', alignItems: 'flex-end', padding: 12, color: ink, overflow: 'hidden' }}>
      <EditableImage path={path} value={value} emptyLabel={label} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      {!value?.url && <span style={{ ...T.mono, fontSize: 10.5, background: dark ? 'rgba(0,0,0,.45)' : 'rgba(255,255,255,.7)', padding: '4px 6px', borderRadius: 4, position: 'relative' }}>{label}</span>}
    </div>
  );
}

function TrustStrip({ items, dark = false, pathPrefix }: { items: TrustStripItem[]; dark?: boolean; pathPrefix?: string }) {
  const div = dark ? 'rgba(255,255,255,0.12)' : aza.rule;
  return (
    <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${items.length},1fr)`, borderTop: `1px solid ${div}`, borderBottom: `1px solid ${div}` }}>
      {items.map((item, i) => (
        <div key={i} style={{ padding: '20px 22px', borderLeft: i ? `1px solid ${div}` : 'none' }}>
          {pathPrefix
            ? <EditableText path={`${pathPrefix}.${i}.value`} as="div" style={{ ...T.h3, fontSize: 28, color: dark ? aza.paper : aza.ink, fontWeight: 800 }}>{item.value}</EditableText>
            : <div style={{ ...T.h3, fontSize: 28, color: dark ? aza.paper : aza.ink, fontWeight: 800 }}>{item.value}</div>}
          {pathPrefix
            ? <EditableText path={`${pathPrefix}.${i}.label`} as="div" style={{ ...T.small, color: dark ? 'rgba(255,255,255,0.6)' : aza.smoke, marginTop: 4 }}>{item.label}</EditableText>
            : <div style={{ ...T.small, color: dark ? 'rgba(255,255,255,0.6)' : aza.smoke, marginTop: 4 }}>{item.label}</div>}
        </div>
      ))}
    </div>
  );
}

function ServiceCard({ icon, title, blurb, price, href, signal = false, c, titlePath, blurbPath, pricePath }: { icon: ReactNode; title: string; blurb: string; price: string; href: string; signal?: boolean; c: AutozonasaltasContent; titlePath?: string; blurbPath?: string; pricePath?: string }) {
  return (
    <a href={href} target="_top" style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 20, borderRadius: 10, background: aza.paper, border: `1px solid ${aza.rule}`, boxShadow: SHADOW.card, position: 'relative' }}>
      {signal && <span style={{ position: 'absolute', top: 14, right: 14, ...T.micro, background: aza.signalSoft, color: aza.signal, padding: '3px 8px', borderRadius: 999, border: '1px solid #F2C9AC' }}><EditableText path="labels.specialtyBadge" as="span">{c.labels.specialtyBadge}</EditableText></span>}
      <div style={{ width: 42, height: 42, borderRadius: 8, background: aza.teal50, color: aza.teal800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${aza.teal100}` }}>{icon}</div>
      {titlePath ? <EditableText path={titlePath} as="div" style={{ ...T.h4 }}>{title}</EditableText> : <div style={{ ...T.h4 }}>{title}</div>}
      {blurbPath ? <EditableText path={blurbPath} as="div" multiline style={{ ...T.small, color: aza.smoke, minHeight: 36 }}>{blurb}</EditableText> : <div style={{ ...T.small, color: aza.smoke, minHeight: 36 }}>{blurb}</div>}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
        {pricePath ? <EditableText path={pricePath} as="span" style={{ ...T.small, color: aza.ink, fontWeight: 700 }}>{price}</EditableText> : <span style={{ ...T.small, color: aza.ink, fontWeight: 700 }}>{price}</span>}
        <span style={{ ...T.small, color: aza.teal700, fontWeight: 600 }}><EditableText path="labels.viewServiceLabel" as="span">{c.labels.viewServiceLabel}</EditableText> →</span>
      </div>
    </a>
  );
}

function FAQItem({ q, a, defaultOpen = false, questionPath, answerPath }: { q: string; a?: string; defaultOpen?: boolean; questionPath?: string; answerPath?: string }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: `1px solid ${aza.rule}`, padding: '18px 4px' }}>
      <button type="button" onClick={() => setOpen((o) => !o)} style={{ all: 'unset', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, width: '100%' }}>
        {questionPath ? <EditableText path={questionPath} as="span" style={{ ...T.h4, color: aza.ink, fontSize: 17 }}>{q}</EditableText> : <span style={{ ...T.h4, color: aza.ink, fontSize: 17 }}>{q}</span>}
        <span style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 999, background: aza.bone, display: 'flex', alignItems: 'center', justifyContent: 'center', color: aza.ink }}>{open ? <Icon.minus size={14} /> : <Icon.plus size={14} />}</span>
      </button>
      {open && a && (answerPath ? <EditableText path={answerPath} as="div" multiline style={{ ...T.body, color: aza.smoke, marginTop: 10, maxWidth: 720 }}>{a}</EditableText> : <div style={{ ...T.body, color: aza.smoke, marginTop: 10, maxWidth: 720 }}>{a}</div>)}
    </div>
  );
}

function PriceTier({ name, from, includes, highlight = false, cta, note, waHref, c, namePath, fromPath, notePath, includePaths, badge, badgePath, fromLabel, fromLabelPath }: { name: string; from: string; includes: string[]; highlight?: boolean; cta: ReactNode; note?: string; waHref?: string; c: AutozonasaltasContent; namePath?: string; fromPath?: string; notePath?: string; includePaths?: string[]; badge?: string; badgePath?: string; fromLabel?: string; fromLabelPath?: string }) {
  return (
    <div style={{ borderRadius: 12, padding: 24, background: highlight ? aza.teal900 : aza.paper, color: highlight ? aza.paper : aza.ink, border: `1px solid ${highlight ? aza.teal800 : aza.rule}`, boxShadow: highlight ? SHADOW.lift : SHADOW.card, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {highlight && badge && <Badge tone="signal" style={{ alignSelf: 'flex-start' }}>{badgePath ? <EditableText path={badgePath} as="span">{badge}</EditableText> : badge}</Badge>}
      <div>
        {namePath ? <EditableText path={namePath} as="div" style={{ ...T.eyebrow, color: highlight ? 'rgba(255,255,255,.6)' : aza.smoke }}>{name}</EditableText> : <div style={{ ...T.eyebrow, color: highlight ? 'rgba(255,255,255,.6)' : aza.smoke }}>{name}</div>}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 6 }}>
          <span style={{ ...T.small, color: highlight ? 'rgba(255,255,255,.7)' : aza.smoke }}>{fromLabelPath ? <EditableText path={fromLabelPath} as="span">{fromLabel ?? c.labels.priceTierFromLabel}</EditableText> : <EditableText path="labels.priceTierFromLabel" as="span">{c.labels.priceTierFromLabel}</EditableText>}</span>
          {fromPath ? <EditableText path={fromPath} as="span" style={{ ...T.h2, fontSize: 40, fontWeight: 800, color: highlight ? aza.paper : aza.ink }}>€{from}</EditableText> : <span style={{ ...T.h2, fontSize: 40, fontWeight: 800, color: highlight ? aza.paper : aza.ink }}>€{from}</span>}
        </div>
        {note && (notePath ? <EditableText path={notePath} as="div" style={{ ...T.small, color: highlight ? 'rgba(255,255,255,.7)' : aza.smoke }}>{note}</EditableText> : <div style={{ ...T.small, color: highlight ? 'rgba(255,255,255,.7)' : aza.smoke }}>{note}</div>)}
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {includes.map((line, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, ...T.body, color: highlight ? 'rgba(255,255,255,.92)' : aza.ink, fontSize: 14 }}>
            <span style={{ flexShrink: 0, color: highlight ? aza.teal100 : aza.teal700, marginTop: 2 }}><Icon.check size={15} /></span>{includePaths?.[i] ? <EditableText path={includePaths[i]} as="span">{line}</EditableText> : line}
          </li>
        ))}
      </ul>
      <BtnWA label={cta} href={waHref} full size="md" />
    </div>
  );
}

function ReviewCard({ r, pathPrefix }: { r: ReviewItem; pathPrefix?: string }) {
  return (
    <div style={{ padding: 22, background: aza.paper, border: `1px solid ${aza.rule}`, borderRadius: 10, boxShadow: SHADOW.card }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <Stars value={r.rating} size={14} /><span style={{ ...T.micro, color: aza.smoke }}>Google</span>
      </div>
      {pathPrefix ? <EditableText path={`${pathPrefix}.body`} as="div" multiline style={{ ...T.body, color: aza.ink, fontSize: 15, lineHeight: 1.55 }}>"{r.body}"</EditableText> : <div style={{ ...T.body, color: aza.ink, fontSize: 15, lineHeight: 1.55 }}>"{r.body}"</div>}
      <div style={{ ...T.small, color: aza.smoke, marginTop: 12, display: 'flex', justifyContent: 'space-between' }}>
        {pathPrefix ? <EditableText path={`${pathPrefix}.name`} as="span" style={{ fontWeight: 600, color: aza.ink }}>{r.name}</EditableText> : <span style={{ fontWeight: 600, color: aza.ink }}>{r.name}</span>}
        {pathPrefix ? <EditableText path={`${pathPrefix}.when`} as="span">{r.when}</EditableText> : <span>{r.when}</span>}
      </div>
    </div>
  );
}

/* ─── Chrome: header / footer / sticky CTA ────────────────────────── */
function SiteHeader({ lang, current, c, waHref }: { lang: Lang; current: string; c: AutozonasaltasContent; waHref?: string }) {
  const nav = navFor(lang, c);
  const tel = sanitizePhoneHref(c.business.phone);
  return (
    <header style={{ background: aza.paper, color: aza.ink, borderBottom: `1px solid ${aza.rule}` }}>
      <div className="aza-inner aza-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 56px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 26 }}>
          <a href={lang === 'pt' ? '/' : `/${lang}/`} target="_top" style={{ ...T.h4, fontSize: 17, color: aza.ink, letterSpacing: '-0.01em' }}>
            <EditableText path="business.name" as="span">{c.business.name}</EditableText>
          </a>
          <nav className="aza-nav" style={{ display: 'flex', gap: 22 }}>
            {nav.map(([href, label, path]) => (
              <a key={href} href={href} target="_top" style={{ color: current === href ? aza.ink : aza.smoke, ...T.body, fontSize: 14, fontWeight: 600, borderBottom: current === href ? `2px solid ${aza.teal500}` : '2px solid transparent', paddingBottom: 2 }}>
                <EditableText path={path} as="span">{label}</EditableText>
              </a>
            ))}
          </nav>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <LangSwitch lang={lang} />
          {tel && <a className="aza-hide-mobile" href={tel} onClick={onCta('phone_click', 'header')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: aza.ink, ...T.body, fontSize: 14, fontWeight: 600 }}><Icon.phone size={15} /><EditableText path="business.phone" as="span" richText={false}>{c.business.phone}</EditableText></a>}
          <BtnWA label={<EditableText path="labels.bookCtaPt" as="span">{c.labels.bookCtaPt}</EditableText>} href={waHref} size="sm" />
        </div>
      </div>
    </header>
  );
}
function LangSwitch({ lang }: { lang: Lang }) {
  const map: Record<Lang, string> = { pt: '/', en: '/en/', de: '/de/' };
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: `1px solid ${aza.rule}`, padding: 3, borderRadius: 6 }}>
      <Icon.globe size={13} />
      {(['pt', 'en', 'de'] as Lang[]).map((l) => (
        <a key={l} href={map[l]} target="_top" style={{ padding: '3px 7px', borderRadius: 4, ...T.micro, color: l === lang ? aza.ink : aza.smoke, background: l === lang ? aza.bone : 'transparent', fontWeight: 700, textTransform: 'uppercase' }}>{l}</a>
      ))}
    </div>
  );
}
function StickyMobileCTAs({ lang, waHref, tel, c }: { lang: Lang; waHref?: string; tel?: string; c: AutozonasaltasContent }) {
  return (
    <div className="aza-sticky-cta" style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 60, background: aza.paper, borderTop: `1px solid ${aza.rule}`, padding: '10px 12px', gridTemplateColumns: '1fr 1fr', gap: 8, boxShadow: '0 -8px 20px rgba(12,31,36,0.06)' }}>
      <BtnWA label={<EditableText path="labels.waCtaPt" as="span">{c.labels.waCtaPt}</EditableText>} href={waHref} full size="md" />
      <BtnGhost label={<EditableText path="labels.callCtaPt" as="span">{c.labels.callCtaPt}</EditableText>} href={tel} icon={<Icon.phone size={16} />} full size="md" onClick={onCta('phone_click', 'sticky')} />
    </div>
  );
}
function SiteFooter({ lang, c }: { lang: Lang; c: AutozonasaltasContent }) {
  const tSvc = lang === 'en' ? c.labels.footerSectionServicesEn : lang === 'de' ? c.labels.footerSectionServicesDe : c.labels.footerSectionServicesPt;
  const tCmp = lang === 'en' ? c.labels.footerSectionCompanyEn : lang === 'de' ? c.labels.footerSectionCompanyDe : c.labels.footerSectionCompanyPt;
  const tHrs = lang === 'en' ? c.labels.footerSectionHoursEn : lang === 'de' ? c.labels.footerSectionHoursDe : c.labels.footerSectionHoursPt;
  const tSvcPath = lang === 'en' ? 'labels.footerSectionServicesEn' : lang === 'de' ? 'labels.footerSectionServicesDe' : 'labels.footerSectionServicesPt';
  const tCmpPath = lang === 'en' ? 'labels.footerSectionCompanyEn' : lang === 'de' ? 'labels.footerSectionCompanyDe' : 'labels.footerSectionCompanyPt';
  const tHrsPath = lang === 'en' ? 'labels.footerSectionHoursEn' : lang === 'de' ? 'labels.footerSectionHoursDe' : 'labels.footerSectionHoursPt';
  const svcLinks = c.services.items.slice(0, 6);
  return (
    <footer style={{ background: aza.teal900, color: aza.paper }}>
      <div className="aza-inner" style={{ padding: '56px 56px 28px' }}>
        <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1.2fr', gap: 36 }}>
          <div>
            <div style={{ ...T.h4, fontSize: 18, marginBottom: 14 }}>
              <EditableText path="business.name" as="span">{c.business.name}</EditableText>
            </div>
            <div style={{ ...T.small, color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}>
              <EditableText path="business.street" as="div">{c.business.street}</EditableText>
              <span><EditableText path="business.postalCode" as="span">{c.business.postalCode}</EditableText>{' '}<EditableText path="business.locality" as="span">{c.business.locality}</EditableText>, <EditableText path="business.region" as="span">{c.business.region}</EditableText></span>
              <EditableText path="business.phone" as="div">{c.business.phone}</EditableText>
              <EditableText path="business.email" as="div">{c.business.email}</EditableText>
            </div>
            <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {!Array.isArray(c.business.payments) && <EditAnchor path="business.payments" value={c.business.payments} />}
              {normalizeTextItems(c.business.payments).map((item, index) => (
                <Badge key={`${item.text}-${index}`} tone="dark">
                  {Array.isArray(c.business.payments)
                    ? <EditableText path={`business.payments.${index}.text`} as="span">{item.text}</EditableText>
                    : item.text}
                </Badge>
              ))}
            </div>
          </div>
          <div>
            <div style={{ ...T.eyebrow, color: 'rgba(255,255,255,0.55)', marginBottom: 12 }}><EditableText path={tSvcPath} as="span">{tSvc}</EditableText></div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {svcLinks.map((s, index) => <li key={s.slug}><a href={servicePathFor(lang, s.slug)} target="_top" style={{ ...T.small, color: aza.paper, fontWeight: 500 }}><EditableText path={`services.items.${index}.${lang === 'en' ? 'nameEn' : 'namePt'}`} as="span">{lang === 'en' ? s.nameEn : s.namePt}</EditableText></a></li>)}
            </ul>
          </div>
          <div>
            <div style={{ ...T.eyebrow, color: 'rgba(255,255,255,0.55)', marginBottom: 12 }}><EditableText path={tCmpPath} as="span">{tCmp}</EditableText></div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[['/sobre', 'Sobre'], ['/testemunhos', 'Avaliações'], ['/marcacao', 'Marcação'], ['/blog', 'Blog']].map(([h, l]) => <li key={h}><a href={h} target="_top" style={{ ...T.small, color: aza.paper, fontWeight: 500 }}>{l}</a></li>)}
            </ul>
          </div>
          <div>
            <div style={{ ...T.eyebrow, color: 'rgba(255,255,255,0.55)', marginBottom: 12 }}><EditableText path={tHrsPath} as="span">{tHrs}</EditableText></div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6, ...T.small, color: 'rgba(255,255,255,0.85)' }}>
              <li><EditableText path="business.hoursWeekday" as="span">{c.business.hoursWeekday}</EditableText></li><li><EditableText path="business.hoursSat" as="span">{c.business.hoursSat}</EditableText></li><li><EditableText path="business.hoursNote" as="span">{c.business.hoursNote}</EditableText></li>
            </ul>
            <div style={{ marginTop: 18 }}><LangSwitch lang={lang} /></div>
          </div>
        </div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.10)', marginTop: 36, paddingTop: 18, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ ...T.micro, color: 'rgba(255,255,255,0.5)' }}>© {new Date().getFullYear()} <EditableText path="business.legalName" as="span">{c.business.legalName}</EditableText> · <EditableText path="business.nif" as="span">{c.business.nif}</EditableText></div>
          <div style={{ display: 'flex', gap: 14, ...T.micro, color: 'rgba(255,255,255,0.65)' }}>
            <span><EditableText path="labels.footerPrivacy" as="span">{c.labels.footerPrivacy}</EditableText></span>
            <span><EditableText path="labels.footerCookies" as="span">{c.labels.footerCookies}</EditableText></span>
            <span><EditableText path="labels.footerComplaints" as="span">{c.labels.footerComplaints}</EditableText></span>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─── Hero (teal block) ───────────────────────────────────────────── */
function Hero({ eyebrow, title, subtitle, titlePath, subtitlePath, eyebrowPath, ratingNote, ratingNotePath, ctaLabel, waHref, secondaryLabel, secondaryHref, ratingValue, reviewCount, photoPath, photoValue, photoLabel }: {
  eyebrow: string; title: string; subtitle: string; titlePath?: string; subtitlePath?: string; eyebrowPath?: string; ratingNote: string; ratingNotePath?: string; ctaLabel: ReactNode; waHref?: string; secondaryLabel: ReactNode; secondaryHref: string; ratingValue: string; reviewCount: number; photoPath: string; photoValue?: ImageValue; photoLabel: string;
}) {
  return (
    <section style={{ background: aza.teal900, color: aza.paper }}>
      <div className="aza-inner" style={{ padding: '64px 56px 0' }}>
        <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1.05fr 1fr', gap: 56, alignItems: 'flex-end' }}>
          <div style={{ paddingBottom: 56 }}>
            <Eyebrow color="rgba(255,255,255,0.6)" style={{ marginBottom: 18 }}>
              {eyebrowPath ? <EditableText path={eyebrowPath} as="span">{eyebrow}</EditableText> : eyebrow}
            </Eyebrow>
            {titlePath
              ? <EditableText path={titlePath} as="h1" multiline style={{ ...T.h1, color: aza.paper, margin: 0, maxWidth: 640 }}>{title}</EditableText>
              : <h1 style={{ ...T.h1, color: aza.paper, margin: 0, maxWidth: 640 }}>{title}</h1>}
            {subtitlePath
              ? <EditableText path={subtitlePath} as="p" multiline style={{ ...T.lead, marginTop: 18, maxWidth: 540, color: 'rgba(255,255,255,0.85)' }}>{subtitle}</EditableText>
              : <p style={{ ...T.lead, marginTop: 18, maxWidth: 540, color: 'rgba(255,255,255,0.85)' }}>{subtitle}</p>}
            <div style={{ display: 'flex', gap: 12, marginTop: 28, flexWrap: 'wrap' }}>
              <BtnWA size="lg" label={ctaLabel} href={waHref} />
              <BtnGhost size="lg" label={secondaryLabel} href={secondaryHref} dark icon={<Icon.arrow size={16} />} />
            </div>
            <div style={{ marginTop: 28, display: 'flex', alignItems: 'center', gap: 22 }}>
              <Stars value={parseFloat(ratingValue.replace(',', '.')) || 4.9} count={reviewCount} valueLabel={ratingValue} countLabel={`(${reviewCount})`} valuePath="business.ratingValue" countPath="business.reviewCount" color="#FFC44D" dark />
              <span style={{ ...T.small, color: 'rgba(255,255,255,0.6)' }}>
                {ratingNotePath ? <EditableText path={ratingNotePath} as="span">{ratingNote}</EditableText> : ratingNote}
              </span>
            </div>
          </div>
          <div style={{ alignSelf: 'stretch', display: 'flex', alignItems: 'flex-end' }}>
            <PhotoSlot path={photoPath} value={photoValue} dark ratio="4/3" minHeight={360} label={photoLabel} />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Pages ───────────────────────────────────────────────────────── */
function HomePT({ c, waHref }: { c: AutozonasaltasContent; waHref?: string }) {
  const d = [
    {
      item: {
        title: c.home.diffProximityTitle || normalizeDiffCard(c.home.diffProximity).title,
        body: c.home.diffProximityBody || normalizeDiffCard(c.home.diffProximity).body,
      },
      titlePath: 'home.diffProximityTitle',
      bodyPath: 'home.diffProximityBody',
      legacyPath: 'home.diffProximity',
      legacyValue: c.home.diffProximity,
    },
    {
      item: {
        title: c.home.diffPriceTitle || normalizeDiffCard(c.home.diffPrice).title,
        body: c.home.diffPriceBody || normalizeDiffCard(c.home.diffPrice).body,
      },
      titlePath: 'home.diffPriceTitle',
      bodyPath: 'home.diffPriceBody',
      legacyPath: 'home.diffPrice',
      legacyValue: c.home.diffPrice,
    },
    {
      item: {
        title: c.home.diffFullTitle || normalizeDiffCard(c.home.diffFull).title,
        body: c.home.diffFullBody || normalizeDiffCard(c.home.diffFull).body,
      },
      titlePath: 'home.diffFullTitle',
      bodyPath: 'home.diffFullBody',
      legacyPath: 'home.diffFull',
      legacyValue: c.home.diffFull,
    },
  ];
  const dIcons = [<Icon.pin size={20} key="p" />, <Icon.doc size={20} key="d" />, <Icon.wrench size={20} key="w" />];
  const trust = normalizeTrustStrip(c.home.trustStrip);
  const tierIncludes = normalizePriceTierIncludes(c.home.pricingTierIncludes);
  const tierCards = normalizePricingTierCards(
    c.home.pricingTierCards,
    tierIncludes,
    c.home.pricingTierCtaLabel,
    c.labels.priceTierHighlightBadge,
    c.labels.priceTierFromLabel,
  );
  const tierRows = c.pricing.rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => ['Revisão básica (óleo + filtros)', 'Pré-IPO (check de 32 pontos)', 'Travões frente (pastilhas)'].includes(row.service));
  return (
    <>
      <Hero eyebrow={c.home.eyebrowPt} title={c.home.titlePt} subtitle={c.home.subtitlePt} titlePath="home.titlePt" subtitlePath="home.subtitlePt" eyebrowPath="home.eyebrowPt" ctaLabel={<EditableText path="labels.waCtaPt" as="span">{c.labels.waCtaPt}</EditableText>} waHref={waHref} secondaryLabel={<EditableText path="labels.pricingCtaPt" as="span">{c.labels.pricingCtaPt}</EditableText>} secondaryHref={quoteCtaPathFor('pt')} ratingNote={c.labels.ratingSourceNote} ratingNotePath="labels.ratingSourceNote" ratingValue={c.business.ratingValue} reviewCount={c.business.reviewCount} photoPath="brand.heroImage" photoValue={c.brand.heroImage} photoLabel="{photo: hero -frente da oficina, Estrada da Camacha}" />
      <section style={{ background: aza.teal900, color: aza.paper }}>
        <div className="aza-inner" style={{ padding: '0 56px 8px' }}>
          <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', borderTop: '1px solid rgba(255,255,255,0.10)' }}>
            {d.map(({ item, titlePath, bodyPath, legacyPath, legacyValue }, i) => (
              <div key={i} style={{ padding: '32px 28px', borderLeft: i ? '1px solid rgba(255,255,255,0.10)' : 'none' }}>
                <EditAnchor path={legacyPath} value={legacyValue} multiline showInEditMode />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'rgba(255,255,255,0.7)' }}>{dIcons[i]}</div>
                <EditableText path={titlePath} as="div" style={{ ...T.h4, color: aza.paper, marginTop: 10 }}>{item.title}</EditableText>
                <EditableText path={bodyPath} as="div" multiline style={{ ...T.small, color: 'rgba(255,255,255,0.75)', marginTop: 8 }}>{item.body}</EditableText>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Section bone style={{ padding: '0 56px' }}><TrustStrip items={trust} pathPrefix="home.trustStrip" /></Section>
      <Section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32, flexWrap: 'wrap', gap: 12 }}>
          <div><Eyebrow><EditableText path="home.servicesEyebrow" as="span">{c.home.servicesEyebrow}</EditableText></Eyebrow><EditableText path="home.servicesHeading" as="h2" style={{ ...T.h2, margin: '8px 0 0' }}>{c.home.servicesHeading}</EditableText></div>
          <BtnLink label={<EditableText path="home.servicesLinkLabel" as="span">{c.home.servicesLinkLabel}</EditableText>} href="/servicos" />
        </div>
        <div className="aza-grid aza-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
          {c.services.items.map((s, index) => <ServiceCard key={s.slug} icon={iconByName(s.icon)} title={s.namePt} blurb={s.blurbPt} price={s.priceFrom} signal={s.signal} href={`/servicos/${s.slug}`} c={c} titlePath={`services.items.${index}.namePt`} blurbPath={`services.items.${index}.blurbPt`} pricePath={`services.items.${index}.priceFrom`} />)}
        </div>
      </Section>
      <Section bone style={{ display: 'none' }}>
        <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 48, alignItems: 'center' }}>
          <div>
            <Eyebrow><EditableText path="home.pricingEyebrow" as="span">{c.home.pricingEyebrow}</EditableText></Eyebrow>
            <EditableText path="home.pricingHeading" as="h2" style={{ ...T.h2, margin: '8px 0 14px' }}>{c.home.pricingHeading}</EditableText>
            <EditableText path="home.pricingBody" as="p" multiline style={{ ...T.body, color: aza.smoke, maxWidth: 360 }}>{c.home.pricingBody}</EditableText>
            <div style={{ marginTop: 20 }}><BtnGhost label={<EditableText path="home.pricingLinkLabel" as="span">{c.home.pricingLinkLabel}</EditableText>} href={quoteCtaPathFor('pt')} icon={<Icon.arrow size={16} />} onClick={onCta('cta_click', 'home-pricing')} /></div>
          </div>
          <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            {tierRows.map(({ row, index }, i) => {
              const tierCard = tierCards[i] ?? {
                badge: i === 1 ? c.labels.priceTierHighlightBadge : '',
                fromLabel: c.labels.priceTierFromLabel,
                ctaLabel: c.home.pricingTierCtaLabel,
                include1: tierIncludes[0]?.label ?? '',
                include2: tierIncludes[1]?.label ?? '',
                include3: tierIncludes[2]?.label ?? '',
              };
              return <PriceTier key={row.service} name={(row.service.split('(')[0] ?? row.service).trim()} from={row.citadino.replace('€', '')} highlight={i === 1} includes={[tierCard.include1, tierCard.include2, tierCard.include3]} cta={<EditableText path={`home.pricingTierCards.${i}.ctaLabel`} as="span">{tierCard.ctaLabel}</EditableText>} waHref={waHref} c={c} namePath={`pricing.rows.${index}.service`} fromPath={`pricing.rows.${index}.citadino`} notePath={row.note ? `pricing.rows.${index}.note` : undefined} includePaths={[`home.pricingTierCards.${i}.include1`, `home.pricingTierCards.${i}.include2`, `home.pricingTierCards.${i}.include3`]} badge={tierCard.badge} badgePath={`home.pricingTierCards.${i}.badge`} fromLabel={tierCard.fromLabel} fromLabelPath={`home.pricingTierCards.${i}.fromLabel`} />;
            })}
          </div>
        </div>
      </Section>
      <Section>
        <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 56 }}>
          <div>
            <Eyebrow><EditableText path="home.aboutEyebrow" as="span">{c.home.aboutEyebrow}</EditableText></Eyebrow>
            <EditableText path="about.title" as="h2" style={{ ...T.h2, margin: '8px 0 14px' }}>{c.about.title}</EditableText>
            <EditableText path="about.body" as="p" multiline style={{ ...T.lead, color: aza.smoke }}>{c.about.body}</EditableText>
            <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginTop: 28 }}>
              {!Array.isArray(c.about.credentials) && <EditAnchor path="about.credentials" value={c.about.credentials} multiline />}
              {normalizeCredentials(c.about.credentials).map((item, i) => (
                <div key={i} style={{ borderLeft: `2px solid ${aza.teal500}`, paddingLeft: 14 }}>
                  {Array.isArray(c.about.credentials)
                    ? <EditableText path={`about.credentials.${i}.title`} as="div" style={{ ...T.h4, fontSize: 17 }}>{item.title}</EditableText>
                    : <div style={{ ...T.h4, fontSize: 17 }}>{item.title}</div>}
                  {Array.isArray(c.about.credentials)
                    ? <EditableText path={`about.credentials.${i}.description`} as="div" style={{ ...T.small, color: aza.smoke, marginTop: 2 }}>{item.description}</EditableText>
                    : <div style={{ ...T.small, color: aza.smoke, marginTop: 2 }}>{item.description}</div>}
                </div>
              ))}
            </div>
          </div>
          <PhotoSlot path="brand.teamImage" value={c.brand.teamImage} ratio="1/1" label="{photo: equipa em frente à porta da oficina}" />
        </div>
      </Section>
      <Section bone>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div><Eyebrow><EditableText path="home.reviewsEyebrow" as="span">{c.home.reviewsEyebrow}</EditableText></Eyebrow><EditableText path="home.reviewsHeading" as="h2" style={{ ...T.h2, margin: '8px 0 6px' }}>{c.home.reviewsHeading}</EditableText><Stars value={parseFloat(c.business.ratingValue.replace(',', '.')) || 4.9} count={c.business.reviewCount} valueLabel={c.business.ratingValue} countLabel={`(${c.business.reviewCount})`} valuePath="business.ratingValue" countPath="business.reviewCount" /></div>
          <BtnLink label={<><EditableText path="home.reviewsLinkLabel" as="span">{c.home.reviewsLinkLabel}</EditableText></>} href="/testemunhos" />
        </div>
        <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
          {c.reviews.items.filter((r) => r.locale === 'pt').slice(0, 3).map((r) => {
            const index = c.reviews.items.indexOf(r);
            return <ReviewCard key={`${r.name}-${index}`} r={r} pathPrefix={index >= 0 ? `reviews.items.${index}` : undefined} />;
          })}
        </div>
      </Section>
      <ContactTeaser c={c} lang="pt" waHref={waHref} />
    </>
  );
}

function AboutPage({ c, waHref }: { c: AutozonasaltasContent; waHref?: string }) {
  const credentials = normalizeCredentials(c.about.credentials);
  return (
    <>
      <PageHead
        crumb="Inicio / Sobre"
        eyebrow={c.home.aboutEyebrow}
        eyebrowPath="home.aboutEyebrow"
        title={c.about.title}
        titlePath="about.title"
        lead={c.about.body}
        leadPath="about.body"
      />
      <Section>
        <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 56, alignItems: 'start' }}>
          <div>
            <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
              {!Array.isArray(c.about.credentials) && <EditAnchor path="about.credentials" value={c.about.credentials} multiline showInEditMode />}
              {credentials.map((item, i) => (
                <div key={i} style={{ padding: 20, background: aza.bone, border: `1px solid ${aza.rule}`, borderRadius: 10 }}>
                  {Array.isArray(c.about.credentials)
                    ? <EditableText path={`about.credentials.${i}.title`} as="div" style={{ ...T.h4, fontSize: 17 }}>{item.title}</EditableText>
                    : <div style={{ ...T.h4, fontSize: 17 }}>{item.title}</div>}
                  {Array.isArray(c.about.credentials)
                    ? <EditableText path={`about.credentials.${i}.description`} as="div" style={{ ...T.small, color: aza.smoke, marginTop: 4 }}>{item.description}</EditableText>
                    : <div style={{ ...T.small, color: aza.smoke, marginTop: 4 }}>{item.description}</div>}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 28, flexWrap: 'wrap' }}>
              <BtnWA label={<EditableText path="labels.waCtaPt" as="span">{c.labels.waCtaPt}</EditableText>} href={waHref} />
              <BtnGhost label={<EditableText path="labels.allServicesCtaPt" as="span">{c.labels.allServicesCtaPt}</EditableText>} href="/servicos" icon={<Icon.arrow size={15} />} />
            </div>
          </div>
          <PhotoSlot path="brand.teamImage" value={c.brand.teamImage} ratio="4/3" label="{photo: equipa em frente a porta da oficina}" />
        </div>
      </Section>
      <Section bone>
        <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28, alignItems: 'center' }}>
          <PhotoSlot path="brand.workshopImage" value={c.brand.workshopImage} ratio="4/3" label="{photo: oficina Auto Zonas Altas}" />
          <div>
            <Eyebrow><EditableText path="labels.findPt" as="span">{c.labels.findPt}</EditableText></Eyebrow>
            <h2 style={{ ...T.h2, margin: '8px 0 14px' }}>
              <EditableText path="business.street" as="span">{c.business.street}</EditableText>
            </h2>
            <p style={{ ...T.body, color: aza.smoke, margin: 0 }}>
              <EditableText path="business.street" as="span">{c.business.street}</EditableText>,{' '}
              <EditableText path="business.postalCode" as="span">{c.business.postalCode}</EditableText>{' '}
              <EditableText path="business.locality" as="span">{c.business.locality}</EditableText>
            </p>
            <div style={{ marginTop: 20 }}>
              <BtnGhost label={<EditableText path="labels.directionsCtaPt" as="span">{c.labels.directionsCtaPt}</EditableText>} href={sanitizeMapUrl(c.business.mapUrl) || '#'} icon={<Icon.arrow size={15} />} onClick={onCta('directions_click', 'about')} />
            </div>
          </div>
        </div>
      </Section>
      <ContactTeaser c={c} lang="pt" waHref={waHref} />
    </>
  );
}

function ContactTeaser({ c, lang, waHref }: { c: AutozonasaltasContent; lang: Lang; waHref?: string }) {
  return (
    <section style={{ background: aza.paper }}>
      <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
        <PhotoSlot path="brand.workshopImage" value={c.brand.workshopImage} ratio="auto" minHeight={360} label="{map: Google Maps -Estr. da Camacha, São Gonçalo}" />
        <div style={{ padding: '64px 56px', background: aza.teal900, color: aza.paper }}>
          <Eyebrow color="rgba(255,255,255,0.6)"><EditableText path={lang === 'en' ? 'labels.findEn' : lang === 'de' ? 'labels.findDe' : 'labels.findPt'} as="span">{lang === 'en' ? c.labels.findEn : lang === 'de' ? c.labels.findDe : c.labels.findPt}</EditableText></Eyebrow>
          <h2 style={{ ...T.h2, margin: '10px 0 18px', color: aza.paper }}><EditableText path="business.street" as="span">{c.business.street}</EditableText>.</h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 14, color: 'rgba(255,255,255,0.85)', ...T.body }}>
            <li style={{ display: 'flex', gap: 12 }}><Icon.pin size={18} /><EditableText path="business.street" as="span">{c.business.street}</EditableText>, <EditableText path="business.postalCode" as="span">{c.business.postalCode}</EditableText> <EditableText path="business.locality" as="span">{c.business.locality}</EditableText></li>
            <li style={{ display: 'flex', gap: 12 }}><Icon.clock size={18} /><EditableText path="business.hoursWeekday" as="span">{c.business.hoursWeekday}</EditableText>{' · '}<EditableText path="business.hoursSat" as="span">{c.business.hoursSat}</EditableText></li>
            <li style={{ display: 'flex', gap: 12 }}><Icon.phone size={18} /><EditableText path="business.phone" as="span" richText={false}>{c.business.phone}</EditableText></li>
          </ul>
          <div style={{ display: 'flex', gap: 10, marginTop: 26, flexWrap: 'wrap' }}>
            <BtnWA label={<EditableText path={lang === 'en' ? 'labels.waCtaEn' : lang === 'de' ? 'labels.waCtaDe' : 'labels.waCtaPt'} as="span">{lang === 'en' ? c.labels.waCtaEn : lang === 'de' ? c.labels.waCtaDe : c.labels.waCtaPt}</EditableText>} href={waHref} />
            <BtnGhost label={<EditableText path={lang === 'en' ? 'labels.directionsCtaEn' : lang === 'de' ? 'labels.directionsCtaDe' : 'labels.directionsCtaPt'} as="span">{lang === 'en' ? c.labels.directionsCtaEn : lang === 'de' ? c.labels.directionsCtaDe : c.labels.directionsCtaPt}</EditableText>} href={sanitizeMapUrl(c.business.mapUrl) || '#'} dark icon={<Icon.arrow size={15} />} onClick={onCta('directions_click', 'contact-teaser')} />
          </div>
        </div>
      </div>
    </section>
  );
}

function PageHead({ crumb, eyebrow, eyebrowPath, title, lead, titlePath, leadPath }: { crumb?: string; eyebrow: string; eyebrowPath?: string; title: string; lead?: string; titlePath?: string; leadPath?: string }) {
  return (
    <section style={{ background: aza.bone }}>
      <div className="aza-inner" style={{ padding: '52px 56px 32px' }}>
        {crumb && <div style={{ ...T.small, color: aza.smoke, marginBottom: 12 }}>{crumb}</div>}
        <Eyebrow>{eyebrowPath ? <EditableText path={eyebrowPath} as="span">{eyebrow}</EditableText> : eyebrow}</Eyebrow>
        {titlePath
          ? <EditableText path={titlePath} as="h1" style={{ ...T.h1, margin: '8px 0 14px' }}>{title}</EditableText>
          : <h1 style={{ ...T.h1, margin: '8px 0 14px' }}>{title}</h1>}
        {lead && leadPath
          ? <EditableText path={leadPath} as="p" multiline style={{ ...T.lead, color: aza.smoke, maxWidth: 680 }}>{lead}</EditableText>
          : lead && <p style={{ ...T.lead, color: aza.smoke, maxWidth: 680 }}>{lead}</p>}
      </div>
    </section>
  );
}

function ServicesIndex({ c, lang, waHref }: { c: AutozonasaltasContent; lang: Lang; waHref?: string }) {
  return (
    <>
      <PageHead crumb={lang === 'en' ? 'Home / Services' : 'Início / Serviços'} eyebrow={L[lang].services} title={c.servicesPage.title} lead={c.servicesPage.lead} titlePath="servicesPage.title" leadPath="servicesPage.lead" />
      <Section>
        <EditableList<ServiceItem> path="services.items" items={c.services.items} as="div" className="aza-grid aza-grid-4"
          emptyTemplate={() => ({ slug: 'novo', icon: 'wrench', namePt: '', nameEn: '', blurbPt: '', blurbEn: '', priceFrom: '' })}>
          {(s) => {
            const index = c.services.items.indexOf(s);
            return <ServiceCard icon={iconByName(s.icon)} title={lang === 'en' ? s.nameEn : s.namePt} blurb={lang === 'en' ? s.blurbEn : s.blurbPt} price={s.priceFrom} signal={s.signal} href={servicePathFor(lang, s.slug)} c={c} titlePath={index >= 0 ? `services.items.${index}.${lang === 'en' ? 'nameEn' : 'namePt'}` : undefined} blurbPath={index >= 0 ? `services.items.${index}.${lang === 'en' ? 'blurbEn' : 'blurbPt'}` : undefined} pricePath={index >= 0 ? `services.items.${index}.priceFrom` : undefined} />;
          }}
        </EditableList>
        <div style={{ marginTop: 48, padding: 28, background: aza.teal900, color: aza.paper, borderRadius: 12 }} className="aza-grid">
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', alignItems: 'center', gap: 28 }} className="aza-grid">
            <div><Eyebrow color="rgba(255,255,255,0.6)"><EditableText path="servicesPage.promoEyebrow" as="span">{c.servicesPage.promoEyebrow}</EditableText></Eyebrow><EditableText path="servicesPage.promoHeading" as="h3" multiline style={{ ...T.h3, color: aza.paper, marginTop: 8 }}>{c.servicesPage.promoHeading}</EditableText></div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}><BtnWA label={<EditableText path="servicesPage.promoCtaLabel" as="span">{c.servicesPage.promoCtaLabel}</EditableText>} href={waHref} size="lg" /></div>
          </div>
        </div>
      </Section>
    </>
  );
}

const paymentAcceptedValue = (value?: { text: string }[] | string): string =>
  normalizeTextItems(value).map((item) => item.text).join(' · ');

function ServicePage({ slug, c, lang, waHref }: { slug: string; c: AutozonasaltasContent; lang: Lang; waHref?: string }) {
  const svc = c.services.items.find((s) => s.slug === slug);
  const sp = c.servicePages.items.find((s) => s.slug === slug);
  const spIndex = c.servicePages.items.findIndex((s) => s.slug === slug);
  const name = svc ? (lang === 'en' ? svc.nameEn : svc.namePt) : slug;
  const heroTitle = sp?.heroTitle || (lang === 'en' ? `${name} in São Gonçalo, Funchal -Zonas Altas.` : `${name} em São Gonçalo, Funchal -Zonas Altas.`);
  const heroSub = sp?.heroSubtitle || (lang === 'en' ? svc?.blurbEn : svc?.blurbPt) || '';
  const steps = normalizeServiceSteps(sp?.steps);
  const faqs = normalizeServiceFaqs(sp?.faqs);
  const when = normalizeTextItems(sp?.whenBullets);
  const related = c.services.items.filter((s) => s.slug !== slug && s.signal === svc?.signal).slice(0, 3);
  return (
    <>
      <section style={{ background: aza.teal900, color: aza.paper }}>
        <div className="aza-inner" style={{ padding: '48px 56px 56px' }}>
          <div style={{ ...T.small, color: 'rgba(255,255,255,0.55)', marginBottom: 14 }}>{lang === 'en' ? 'Home / Services / ' : 'Início / Serviços / '}{name}</div>
          <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 56, alignItems: 'flex-end' }}>
            <div>
              {sp?.badge && (spIndex >= 0 ? <Badge tone="signal" style={{ marginBottom: 14 }}><EditableText path={`servicePages.items.${spIndex}.badge`} as="span">{sp.badge}</EditableText></Badge> : <Badge tone="signal" style={{ marginBottom: 14 }}>{sp.badge}</Badge>)}
              {spIndex >= 0 ? <EditableText path={`servicePages.items.${spIndex}.heroTitle`} as="h1" multiline style={{ ...T.h1, color: aza.paper, margin: 0 }}>{heroTitle}</EditableText> : <h1 style={{ ...T.h1, color: aza.paper, margin: 0 }}>{heroTitle}</h1>}
              {spIndex >= 0 ? <EditableText path={`servicePages.items.${spIndex}.heroSubtitle`} as="p" multiline style={{ ...T.lead, color: 'rgba(255,255,255,0.85)', marginTop: 16, maxWidth: 560 }}>{heroSub}</EditableText> : <p style={{ ...T.lead, color: 'rgba(255,255,255,0.85)', marginTop: 16, maxWidth: 560 }}>{heroSub}</p>}
              <div style={{ display: 'flex', gap: 10, marginTop: 24, flexWrap: 'wrap' }}>
                <BtnWA size="lg" label={<EditableText path={lang === 'en' ? 'labels.waCtaEn' : 'labels.waCtaPt'} as="span">{lang === 'en' ? c.labels.waCtaEn : c.labels.waCtaPt}</EditableText>} href={waHref} />
                <BtnGhost size="lg" label={<EditableText path="servicePage.emailCtaLabel" as="span">{c.servicePage.emailCtaLabel}</EditableText>} href={quotePathFor(lang, slug)} dark />
              </div>
            </div>
            <PhotoSlot path={slug === 'pintura' ? 'brand.paintImage' : 'brand.bodyShopImage'} value={slug === 'pintura' ? c.brand.paintImage : c.brand.bodyShopImage} dark ratio="4/3" label={`{photo: ${name}}`} />
          </div>
        </div>
      </section>
      <Section bone style={{ padding: '0 56px' }}>
        <TrustStrip items={normalizeTrustStrip(c.servicePage.trustStrip)} pathPrefix="servicePage.trustStrip" />
      </Section>
      {when.length > 0 && (
        <Section>
          <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 56 }}>
            <div><Eyebrow><EditableText path="servicePage.whenEyebrow" as="span">{c.servicePage.whenEyebrow}</EditableText></Eyebrow><h2 style={{ ...T.h2, margin: '8px 0 14px' }}><EditableText path="servicePage.whenHeadingPrefix" as="span">{c.servicePage.whenHeadingPrefix}</EditableText> {name.toLowerCase()}?</h2><EditableText path="servicePage.whenBody" as="p" multiline style={{ ...T.body, color: aza.smoke, maxWidth: 460 }}>{c.servicePage.whenBody}</EditableText></div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {when.map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 18px', background: aza.bone, borderRadius: 10, border: `1px solid ${aza.rule}` }}>
                  <span style={{ ...T.h3, fontSize: 22, color: aza.teal700, fontWeight: 800, minWidth: 30 }}>{String(i + 1).padStart(2, '0')}</span>
                  {spIndex >= 0
                    ? <EditableText path={`servicePages.items.${spIndex}.whenBullets.${i}.text`} as="span" multiline style={{ ...T.body, color: aza.ink }}>"{item.text}"</EditableText>
                    : <span style={{ ...T.body, color: aza.ink }}>"{item.text}"</span>}
                </li>
              ))}
            </ul>
          </div>
        </Section>
      )}
      {steps.length > 0 && (
        <Section bone>
          <Eyebrow><EditableText path="servicePage.stepsEyebrow" as="span">{c.servicePage.stepsEyebrow}</EditableText></Eyebrow><EditableText path="servicePage.stepsHeading" as="h2" style={{ ...T.h2, margin: '8px 0 36px' }}>{c.servicePage.stepsHeading}</EditableText>
          <div className="aza-grid aza-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 18 }}>
            {steps.map((step, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                  <span style={{ ...T.mono, color: aza.signal, fontSize: 13 }}><EditableText path="servicePage.stepLabel" as="span">{c.servicePage.stepLabel}</EditableText> {String(i + 1).padStart(2, '0')}</span>
                  {step.meta && (spIndex >= 0
                    ? <EditableText path={`servicePages.items.${spIndex}.steps.${i}.meta`} as="span" style={{ ...T.micro, background: aza.signalSoft, color: aza.signal, padding: '3px 8px', borderRadius: 999, border: '1px solid #F2C9AC' }}>{step.meta}</EditableText>
                    : <span style={{ ...T.micro, background: aza.signalSoft, color: aza.signal, padding: '3px 8px', borderRadius: 999, border: '1px solid #F2C9AC' }}>{step.meta}</span>)}
                </div>
                {spIndex >= 0
                  ? <EditableText path={`servicePages.items.${spIndex}.steps.${i}.title`} as="div" style={{ ...T.h4 }}>{step.title}</EditableText>
                  : <div style={{ ...T.h4 }}>{step.title}</div>}
                {spIndex >= 0
                  ? <EditableText path={`servicePages.items.${spIndex}.steps.${i}.body`} as="div" multiline style={{ ...T.small, color: aza.smoke }}>{step.body}</EditableText>
                  : <div style={{ ...T.small, color: aza.smoke }}>{step.body}</div>}
              </div>
            ))}
          </div>
        </Section>
      )}
      {faqs.length > 0 && (
        <Section bone>
          <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 48 }}>
            <div><Eyebrow><EditableText path="servicePage.faqEyebrow" as="span">{c.servicePage.faqEyebrow}</EditableText></Eyebrow><EditableText path="servicePage.faqHeading" as="h2" style={{ ...T.h2, margin: '8px 0 12px' }}>{c.servicePage.faqHeading}</EditableText><div style={{ marginTop: 18 }}><BtnWA label={<EditableText path="servicePage.faqCtaLabel" as="span">{c.servicePage.faqCtaLabel}</EditableText>} href={waHref} /></div></div>
            <div>
              {faqs.map((faq, i) => <FAQItem key={i} q={faq.question} a={faq.answer} defaultOpen={i === 0} questionPath={spIndex >= 0 ? `servicePages.items.${spIndex}.faqs.${i}.question` : undefined} answerPath={spIndex >= 0 ? `servicePages.items.${spIndex}.faqs.${i}.answer` : undefined} />)}
            </div>
          </div>
        </Section>
      )}
      <section style={{ background: aza.teal900, color: aza.paper }}>
        <div className="aza-inner aza-grid" style={{ padding: '64px 56px', display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 56, alignItems: 'center' }}>
          <div><EditableText path="servicePage.ctaHeading" as="h2" style={{ ...T.h2, color: aza.paper, margin: 0 }}>{c.servicePage.ctaHeading}</EditableText><EditableText path="servicePage.ctaBody" as="p" multiline style={{ ...T.lead, color: 'rgba(255,255,255,0.8)', marginTop: 12, maxWidth: 540 }}>{c.servicePage.ctaBody}</EditableText></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}><BtnWA label={<EditableText path={lang === 'en' ? 'labels.waCtaEn' : 'labels.waCtaPt'} as="span">{lang === 'en' ? c.labels.waCtaEn : c.labels.waCtaPt}</EditableText>} href={waHref} full size="lg" /><BtnGhost label={<EditableText path="servicePage.emailCtaLabel" as="span">{c.servicePage.emailCtaLabel}</EditableText>} href={quotePathFor(lang, slug)} dark icon={<Icon.arrow size={15} />} full onClick={onCta('cta_click', 'service-quote')} /></div>
        </div>
      </section>
      {related.length > 0 && (
        <Section>
          <Eyebrow><EditableText path="servicePage.relatedEyebrow" as="span">{c.servicePage.relatedEyebrow}</EditableText></Eyebrow><EditableText path="servicePage.relatedHeading" as="h3" style={{ ...T.h3, margin: '8px 0 22px' }}>{c.servicePage.relatedHeading}</EditableText>
          <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
            {related.map((s) => {
              const index = c.services.items.indexOf(s);
              return <ServiceCard key={s.slug} icon={iconByName(s.icon)} title={lang === 'en' ? s.nameEn : s.namePt} blurb={lang === 'en' ? s.blurbEn : s.blurbPt} price={s.priceFrom} signal={s.signal} href={servicePathFor(lang, s.slug)} c={c} titlePath={index >= 0 ? `services.items.${index}.${lang === 'en' ? 'nameEn' : 'namePt'}` : undefined} blurbPath={index >= 0 ? `services.items.${index}.${lang === 'en' ? 'blurbEn' : 'blurbPt'}` : undefined} pricePath={index >= 0 ? `services.items.${index}.priceFrom` : undefined} />;
            })}
          </div>
        </Section>
      )}
    </>
  );
}

function PricingPage({ c, lang, waHref }: { c: AutozonasaltasContent; lang: Lang; waHref?: string }) {
  const groups = Array.from(new Set(c.pricing.rows.map((r) => r.group)));
  const finePrint = normalizeTextItems(c.pricing.finePrint);
  return (
    <>
      <PageHead crumb={lang === 'en' ? 'Home / Pricing' : lang === 'de' ? 'Start / Preise' : 'Início / Preços'} eyebrow={c.pricingPage.eyebrow} eyebrowPath="pricingPage.eyebrow" title={c.pricingPage.title} lead={c.pricingPage.lead} titlePath="pricingPage.title" leadPath="pricingPage.lead" />
      <Section>
        <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 32 }}>
          <aside style={{ alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div><Eyebrow><EditableText path="pricingPage.categoriesLabel" as="span">{c.pricingPage.categoriesLabel}</EditableText></Eyebrow><ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0', display: 'flex', flexDirection: 'column', gap: 6 }}>{groups.map((g) => { const groupIndex = c.pricing.rows.findIndex((row) => row.group === g); return <li key={g} style={{ ...T.body, color: aza.ink, fontWeight: 500 }}>{groupIndex >= 0 ? <EditableText path={`pricing.rows.${groupIndex}.group`} as="span">{g}</EditableText> : g}</li>; })}</ul></div>
            <div style={{ padding: 18, background: aza.teal900, color: aza.paper, borderRadius: 12 }}>
              <Eyebrow color="rgba(255,255,255,0.6)"><EditableText path="pricingPage.diagEyebrow" as="span">{c.pricingPage.diagEyebrow}</EditableText></Eyebrow>
              <EditableText path="pricingPage.diagHeading" as="div" style={{ ...T.h4, marginTop: 6, color: aza.paper }}>{c.pricingPage.diagHeading}</EditableText>
              <EditableText path="pricingPage.diagBody" as="div" multiline style={{ ...T.small, marginTop: 6, color: 'rgba(255,255,255,0.75)' }}>{c.pricingPage.diagBody}</EditableText>
              <div style={{ marginTop: 14 }}><BtnWA label={<EditableText path="pricingPage.diagCtaLabel" as="span">{c.pricingPage.diagCtaLabel}</EditableText>} href={waHref} full /></div>
            </div>
          </aside>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
            <EditableList<PricingRow> path="pricing.rows" items={c.pricing.rows}
              emptyTemplate={() => ({ service: '', group: '', citadino: '€0', medio: '€0', suv: '€0' })}>
              {() => null}
            </EditableList>
            {groups.map((g) => (
              <div key={g}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
                  <h2 style={{ ...T.h3, fontSize: 22, margin: 0 }}>{g}</h2>
                  <span style={{ ...T.micro, color: aza.smoke }}><EditableText path="pricingPage.tableNoteLabel" as="span">{c.pricingPage.tableNoteLabel}</EditableText></span>
                </div>
                <div style={{ border: `1px solid ${aza.rule}`, borderRadius: 10, overflow: 'hidden', background: aza.paper, boxShadow: SHADOW.card }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2.4fr 1fr 1fr 1fr', padding: '12px 18px', background: aza.bone, borderBottom: `1px solid ${aza.rule}`, ...T.micro, color: aza.smoke, letterSpacing: '0.12em' }}>
                    <span><EditableText path="pricingPage.tableHeaderService" as="span">{c.pricingPage.tableHeaderService}</EditableText></span><span style={{ textAlign: 'right' }}><EditableText path="pricingPage.tableHeaderCitadino" as="span">{c.pricingPage.tableHeaderCitadino}</EditableText></span><span style={{ textAlign: 'right' }}><EditableText path="pricingPage.tableHeaderMedio" as="span">{c.pricingPage.tableHeaderMedio}</EditableText></span><span style={{ textAlign: 'right' }}><EditableText path="pricingPage.tableHeaderSuv" as="span">{c.pricingPage.tableHeaderSuv}</EditableText></span>
                  </div>
                  {c.pricing.rows.filter((r) => r.group === g).map((row, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '2.4fr 1fr 1fr 1fr', padding: '14px 18px', borderTop: i ? `1px solid ${aza.rule}` : 'none', alignItems: 'center' }}>
                      {(() => {
                        const rowIndex = c.pricing.rows.indexOf(row);
                        return (
                          <>
                            <div><div style={{ ...T.body, fontWeight: 600, color: aza.ink }}>{rowIndex >= 0 ? <EditableText path={`pricing.rows.${rowIndex}.service`} as="span">{row.service}</EditableText> : row.service}</div>{row.note && <div style={{ ...T.small, color: aza.smoke }}>{rowIndex >= 0 ? <EditableText path={`pricing.rows.${rowIndex}.note`} as="span">{row.note}</EditableText> : row.note}</div>}</div>
                            <div style={{ ...T.mono, textAlign: 'right', fontSize: 15 }}>{rowIndex >= 0 ? <EditableText path={`pricing.rows.${rowIndex}.citadino`} as="span">{row.citadino}</EditableText> : row.citadino}</div>
                            <div style={{ ...T.mono, textAlign: 'right', fontSize: 15, fontWeight: 600 }}>{rowIndex >= 0 ? <EditableText path={`pricing.rows.${rowIndex}.medio`} as="span">{row.medio}</EditableText> : row.medio}</div>
                            <div style={{ ...T.mono, textAlign: 'right', fontSize: 15 }}>{rowIndex >= 0 ? <EditableText path={`pricing.rows.${rowIndex}.suv`} as="span">{row.suv}</EditableText> : row.suv}</div>
                          </>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div style={{ padding: 22, background: aza.bone, border: `1px solid ${aza.rule}`, borderRadius: 10 }}>
              <Eyebrow><EditableText path="pricingPage.finePrintEyebrow" as="span">{c.pricingPage.finePrintEyebrow}</EditableText></Eyebrow>
              <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0', display: 'flex', flexDirection: 'column', gap: 8, ...T.small, color: aza.ink }}>
                {!Array.isArray(c.pricing.finePrint) && <EditAnchor path="pricing.finePrint" value={c.pricing.finePrint} multiline showInEditMode />}
                {finePrint.map((item, i) => <li key={i}>· {Array.isArray(c.pricing.finePrint) ? <EditableText path={`pricing.finePrint.${i}.text`} as="span">{item.text}</EditableText> : item.text}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}

function BookingPage({ c, lang, waHref, publicWriteToken }: { c: AutozonasaltasContent; lang: Lang; waHref?: string; publicWriteToken?: string }) {
  const { editing } = useEditMode();
  const [leadState, setLeadState] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const nextSteps = normalizeTextItems(c.booking.nextSteps);
  const field = (labelPath: string, labelText: string, placeholderPath: string, placeholderText: string, mono = false) => {
    const name = labelPath.split('.').pop()!.replace(/^formField/, '').toLowerCase();
    return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ ...T.small, fontWeight: 700, color: aza.ink }}><EditableText path={labelPath} as="span">{labelText}</EditableText></span>
                <EditAnchor path={placeholderPath} value={placeholderText} />
      <input name={name} disabled={editing} placeholder={placeholderText} style={{ border: `1px solid ${aza.ruleStrong}`, borderRadius: 6, padding: '10px 12px', ...(mono ? T.mono : T.body), fontSize: 15, color: aza.ink, background: aza.paper, outline: 'none' }} />
    </label>
    );
  };

  async function handleBookingSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (editing || leadState === 'sending') return;
    const form = event.currentTarget;
    const fd = new FormData(form);
    const get = (key: string) => String(fd.get(key) ?? '').trim();
    const message = [
      get('plate') ? `Matrícula: ${get('plate')}` : '',
      get('service') ? `Serviço: ${get('service')}` : '',
      get('date') ? `Data: ${get('date')}${get('period') ? ` (${get('period')})` : ''}` : get('period') ? `Período: ${get('period')}` : '',
      get('symptom'),
    ].filter(Boolean).join('\n');
    setLeadState('sending');
    try {
      const tokenRes = await fetch('/api/public-write-token', { cache: 'no-store' });
      if (!tokenRes.ok) throw new Error('token_fetch_failed');
      const { token: freshToken } = (await tokenRes.json()) as { token?: string };
      if (!freshToken) throw new Error('token_missing');
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          publicWriteToken: freshToken,
          source: 'contact_form',
          name: get('name') || undefined,
          phone: get('phone') || undefined,
          message: message || undefined,
        }),
      });
      if (!res.ok) throw new Error('lead_submit_failed');
      trackEvent('lead_submitted', { form: 'booking' });
      form.reset();
      setLeadState('success');
    } catch {
      setLeadState('error');
    }
  }

  const bookingStatusMessage =
    leadState === 'success'
      ? lang === 'en'
        ? 'Request sent — we will contact you shortly.'
        : lang === 'de'
          ? 'Anfrage gesendet — wir melden uns in Kürze.'
          : 'Pedido enviado — entraremos em contacto em breve.'
      : leadState === 'error'
        ? lang === 'en'
          ? 'Could not send. Please use WhatsApp or call us.'
          : lang === 'de'
            ? 'Senden fehlgeschlagen. Bitte WhatsApp oder Anruf.'
            : 'Não foi possível enviar. Use o WhatsApp ou ligue-nos.'
        : '';
  return (
    <>
      <section style={{ background: aza.bone }}>
        <div className="aza-inner" style={{ padding: '52px 56px 32px' }}>
          <EditableText path="bookingPage.breadcrumb" as="div" style={{ ...T.small, color: aza.smoke, marginBottom: 12 }}>{c.bookingPage.breadcrumb}</EditableText>
          <Eyebrow><EditableText path="bookingPage.sectionEyebrow" as="span">{c.bookingPage.sectionEyebrow}</EditableText></Eyebrow>
          <EditableText path="booking.title" as="h1" style={{ ...T.h1, margin: '8px 0 14px' }}>{c.booking.title}</EditableText>
          <EditableText path="booking.intro" as="p" multiline style={{ ...T.lead, color: aza.smoke, maxWidth: 680 }}>{c.booking.intro}</EditableText>
        </div>
      </section>
      <Section>
        <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 56, alignItems: 'flex-start' }}>
          <form onSubmit={handleBookingSubmit} style={{ background: aza.paper, padding: 28, border: `1px solid ${aza.rule}`, borderRadius: 12, boxShadow: SHADOW.card, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }} className="aza-grid">
            <div style={{ gridColumn: 'span 2' }}>{field('bookingPage.formFieldName', c.bookingPage.formFieldName, 'bookingPage.formFieldNamePlaceholder', c.bookingPage.formFieldNamePlaceholder)}</div>
            {field('bookingPage.formFieldPlate', c.bookingPage.formFieldPlate, 'bookingPage.formFieldPlatePlaceholder', c.bookingPage.formFieldPlatePlaceholder, true)}
            {field('bookingPage.formFieldPhone', c.bookingPage.formFieldPhone, 'bookingPage.formFieldPhonePlaceholder', c.bookingPage.formFieldPhonePlaceholder)}
            <div style={{ gridColumn: 'span 2' }}>{field('bookingPage.formFieldService', c.bookingPage.formFieldService, 'bookingPage.formFieldServicePlaceholder', c.bookingPage.formFieldServicePlaceholder)}</div>
            {field('bookingPage.formFieldDate', c.bookingPage.formFieldDate, 'bookingPage.formFieldDatePlaceholder', c.bookingPage.formFieldDatePlaceholder, true)}
            {field('bookingPage.formFieldPeriod', c.bookingPage.formFieldPeriod, 'bookingPage.formFieldPeriodPlaceholder', c.bookingPage.formFieldPeriodPlaceholder)}
            <label style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ ...T.small, fontWeight: 700, color: aza.ink }}><EditableText path="bookingPage.formFieldSymptom" as="span">{c.bookingPage.formFieldSymptom}</EditableText></span>
              <textarea name="symptom" disabled={editing} style={{ border: `1px solid ${aza.ruleStrong}`, borderRadius: 6, padding: 12, ...T.body, fontSize: 15, minHeight: 96, resize: 'vertical', background: aza.paper, outline: 'none' }} />
            </label>
            <div style={{ gridColumn: 'span 2' }}>
              <button
                type="submit"
                disabled={editing || leadState === 'sending'}
                style={{ width: '100%', background: aza.ink, color: aza.paper, border: 'none', padding: '13px 20px', borderRadius: 6, ...T.body, fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: editing || leadState === 'sending' ? 'not-allowed' : 'pointer', opacity: leadState === 'sending' ? 0.7 : 1 }}
              >
                <EditableText path="bookingPage.emailCtaLabel" as="span">{c.bookingPage.emailCtaLabel}</EditableText>
                <Icon.arrow size={15} />
              </button>
              {bookingStatusMessage ? (
                <p style={{ ...T.small, marginTop: 10, color: leadState === 'error' ? aza.signal : aza.teal700 }}>{bookingStatusMessage}</p>
              ) : null}
            </div>
          </form>
          <aside style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div style={{ padding: 22, background: aza.teal900, color: aza.paper, borderRadius: 12 }}>
              <Eyebrow color="rgba(255,255,255,0.6)"><EditableText path="bookingPage.whatsappEyebrow" as="span">{c.bookingPage.whatsappEyebrow}</EditableText></Eyebrow>
              <EditableText path="bookingPage.whatsappHeading" as="h3" style={{ ...T.h3, color: aza.paper, margin: '8px 0 12px' }}>{c.bookingPage.whatsappHeading}</EditableText>
              <EditableText path="bookingPage.whatsappBody" as="p" multiline style={{ ...T.small, color: 'rgba(255,255,255,0.8)' }}>{c.bookingPage.whatsappBody}</EditableText>
              <div style={{ marginTop: 16 }}><BtnWA label={<EditableText path="bookingPage.whatsappCtaLabel" as="span">{c.bookingPage.whatsappCtaLabel}</EditableText>} href={waHref} full size="lg" /></div>
            </div>
            <div style={{ padding: 22, background: aza.paper, border: `1px solid ${aza.rule}`, borderRadius: 12 }}>
              <EditableText path="bookingPage.nextStepsHeading" as="div" style={{ ...T.h4, marginBottom: 8 }}>{c.bookingPage.nextStepsHeading}</EditableText>
              <ol style={{ paddingLeft: 18, margin: 0, ...T.small, color: aza.ink, lineHeight: 1.65, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {!Array.isArray(c.booking.nextSteps) && <EditAnchor path="booking.nextSteps" value={c.booking.nextSteps} multiline showInEditMode />}
                {nextSteps.map((item, i) => <li key={i}>{Array.isArray(c.booking.nextSteps) ? <EditableText path={`booking.nextSteps.${i}.text`} as="span">{item.text}</EditableText> : item.text}</li>)}
              </ol>
            </div>
          </aside>
        </div>
      </Section>
    </>
  );
}

function ContactPage({ c, lang, waHref }: { c: AutozonasaltasContent; lang: Lang; waHref?: string }) {
  const tel = sanitizePhoneHref(c.business.phone);
  const contactTitle = lang === 'en' ? 'Contact' : lang === 'de' ? 'Kontakt' : 'Contactos';
  const hoursTitle = lang === 'en' ? 'Hours' : lang === 'de' ? 'Öffnungszeiten' : 'Horário';
  return (
    <>
      <PageHead eyebrow={contactTitle} title={c.contact.title} titlePath="contact.title" />
      <section style={{ background: aza.paper }}>
        <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', minHeight: 480 }}>
          <PhotoSlot path="brand.workshopImage" value={c.brand.workshopImage} ratio="auto" minHeight={420} label="{map: Google Maps -Estr. da Camacha, São Gonçalo}" />
          <div style={{ padding: '48px 56px', display: 'flex', flexDirection: 'column', gap: 22 }}>
            <div>
              <Eyebrow>NAP</Eyebrow>
              <EditableText path="business.name" as="div" style={{ ...T.h3, marginTop: 8 }}>{c.business.name}</EditableText>
              <div style={{ ...T.body, color: aza.ink, lineHeight: 1.65, marginTop: 8 }}>
                <EditableText path="business.street" as="span">{c.business.street}</EditableText><br /><EditableText path="business.postalCode" as="span">{c.business.postalCode}</EditableText> <EditableText path="business.locality" as="span">{c.business.locality}</EditableText>, <EditableText path="business.region" as="span">{c.business.region}</EditableText><br /><EditableText path="business.phone" as="span">{c.business.phone}</EditableText><br /><EditableText path="business.email" as="span">{c.business.email}</EditableText>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <BtnWA label={<EditableText path={lang === 'en' ? 'labels.waCtaEn' : 'labels.waCtaPt'} as="span">{lang === 'en' ? c.labels.waCtaEn : c.labels.waCtaPt}</EditableText>} href={waHref} />
              <BtnGhost label={<EditableText path={lang === 'en' ? 'labels.callCtaEn' : 'labels.callCtaPt'} as="span">{lang === 'en' ? c.labels.callCtaEn : c.labels.callCtaPt}</EditableText>} href={tel || '#'} icon={<Icon.phone size={15} />} onClick={onCta('phone_click', 'contact')} />
            </div>
            <hr style={{ border: 'none', borderTop: `1px solid ${aza.rule}`, margin: 0 }} />
            <div>
              <Eyebrow>{hoursTitle}</Eyebrow>
              <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', display: 'flex', flexDirection: 'column', gap: 4, ...T.body, color: aza.ink }}>
                <li><EditableText path="business.hoursWeekday" as="span">{c.business.hoursWeekday}</EditableText></li><li><EditableText path="business.hoursSat" as="span">{c.business.hoursSat}</EditableText></li><li style={{ color: aza.smoke }}><EditableText path="business.hoursNote" as="span">{c.business.hoursNote}</EditableText></li>
              </ul>
            </div>
            <div><Eyebrow>{L[lang].dir}</Eyebrow><p style={{ ...T.small, color: aza.smoke, marginTop: 8 }}><EditableText path="contact.directionsNote" as="span" multiline>{c.contact.directionsNote}</EditableText></p></div>
          </div>
        </div>
      </section>
    </>
  );
}

function ReviewsPage({ c }: { c: AutozonasaltasContent }) {
  return (
    <>
      <PageHead eyebrow={c.reviewsPage.eyebrow} eyebrowPath="reviewsPage.eyebrow" title={c.reviewsPage.titlePt} titlePath="reviewsPage.titlePt" lead={c.reviewsPage.lead} leadPath="reviewsPage.lead" />
      <Section>
        <EditableList<ReviewItem> path="reviews.items" items={c.reviews.items} className="aza-grid aza-grid-3"
          emptyTemplate={() => ({ name: '', locale: 'pt', when: '', body: '', rating: 5 })}>
          {(r) => {
            const index = c.reviews.items.indexOf(r);
            return <ReviewCard r={r} pathPrefix={index >= 0 ? `reviews.items.${index}` : undefined} />;
          }}
        </EditableList>
      </Section>
    </>
  );
}

function mdToBlocks(body: string): ReactNode {
  return body.split('\n\n').map((blk, i) => {
    if (blk.startsWith('## ')) return <h2 key={i} style={{ ...T.h3, marginTop: i ? 28 : 0 }}>{blk.slice(3)}</h2>;
    return <p key={i} style={{ ...T.body, fontSize: 17, lineHeight: 1.75, color: aza.ink }}>{blk}</p>;
  });
}
function BlogIndex({ c }: { c: AutozonasaltasContent }) {
  return (
    <>
      <PageHead eyebrow={c.blogPage.indexEyebrow} eyebrowPath="blogPage.indexEyebrow" title={c.blogPage.indexTitle} titlePath="blogPage.indexTitle" lead={c.blogPage.indexLead} leadPath="blogPage.indexLead" />
      <Section>
        <a href="/blog/quanto-custa-pintar-um-carro" target="_top" style={{ display: 'block', padding: 28, border: `1px solid ${aza.rule}`, borderRadius: 12, background: aza.paper, boxShadow: SHADOW.card }}>
          <Eyebrow><EditableText path="blog.eyebrow" as="span">{c.blog.eyebrow}</EditableText></Eyebrow>
          <EditableText path="blog.title" as="h2" style={{ ...T.h2, margin: '10px 0 10px' }}>{c.blog.title}</EditableText>
          <EditableText path="blog.excerpt" as="p" multiline style={{ ...T.body, color: aza.smoke }}>{c.blog.excerpt}</EditableText>
        </a>
      </Section>
    </>
  );
}
function BlogPost({ c, waHref }: { c: AutozonasaltasContent; waHref?: string }) {
  return (
    <article>
      <section style={{ background: aza.bone }}>
        <div className="aza-inner" style={{ padding: '48px 56px 36px', maxWidth: 880 }}>
          <div style={{ ...T.small, color: aza.smoke, marginBottom: 12 }}>Blog / Bate-chapa &amp; pintura</div>
          <Eyebrow><EditableText path="blog.eyebrow" as="span">{c.blog.eyebrow}</EditableText></Eyebrow>
          <EditableText path="blog.title" as="h1" multiline style={{ ...T.h1, margin: '12px 0 16px' }}>{c.blog.title}</EditableText>
          <EditableText path="blog.excerpt" as="p" multiline style={{ ...T.lead, color: aza.smoke }}>{c.blog.excerpt}</EditableText>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 20, ...T.small, color: aza.ink }}>
            <div style={{ width: 36, height: 36, borderRadius: 999, background: aza.teal700, color: aza.paper, display: 'flex', alignItems: 'center', justifyContent: 'center', ...T.micro, fontWeight: 800 }}>{c.blog.author.split(' ').map((w) => w[0]).join('').slice(0, 2)}</div>
            <div><div style={{ fontWeight: 700 }}><EditableText path="blog.author" as="div">{c.blog.author}</EditableText></div><div style={{ color: aza.smoke }}><EditableText path="blog.authorRole" as="div">{c.blog.authorRole}</EditableText></div></div>
          </div>
        </div>
      </section>
      <PhotoSlot path="brand.blogImage" value={c.brand.blogImage} ratio="21/9" label="{photo: pintor a aplicar verniz sob luz de cabine}" />
      <Section>
        <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 48 }}>
          <div style={{ maxWidth: 660 }}><EditableText path="blog.body" as="div" multiline style={{ display: 'none' }}>{c.blog.body}</EditableText>{mdToBlocks(c.blog.body)}</div>
          <aside>
            <div style={{ padding: 20, background: aza.teal900, color: aza.paper, borderRadius: 12 }}>
              <Eyebrow color="rgba(255,255,255,0.6)"><EditableText path="blogPage.ctaEyebrow" as="span">{c.blogPage.ctaEyebrow}</EditableText></Eyebrow>
              <EditableText path="blogPage.ctaHeading" as="div" style={{ ...T.h4, color: aza.paper, marginTop: 8 }}>{c.blogPage.ctaHeading}</EditableText>
              <EditableText path="blogPage.ctaBody" as="p" multiline style={{ ...T.small, color: 'rgba(255,255,255,0.75)', marginTop: 6 }}>{c.blogPage.ctaBody}</EditableText>
              <div style={{ marginTop: 12 }}><BtnWA full label={<EditableText path="blogPage.ctaLabel" as="span">{c.blogPage.ctaLabel}</EditableText>} href={waHref} /></div>
            </div>
          </aside>
        </div>
      </Section>
    </article>
  );
}

/* EN */
function EnHome({ c, waHref }: { c: AutozonasaltasContent; waHref?: string }) {
  return (
    <>
      <Hero eyebrow={c.enHome.eyebrow} title={c.enHome.title} subtitle={c.enHome.subtitle} titlePath="enHome.title" subtitlePath="enHome.subtitle" eyebrowPath="enHome.eyebrow" ctaLabel={<EditableText path="labels.waCtaEn" as="span">{c.labels.waCtaEn}</EditableText>} waHref={waHref} secondaryLabel={<EditableText path="labels.pricingCtaEn" as="span">{c.labels.pricingCtaEn}</EditableText>} secondaryHref={quoteCtaPathFor('en')} ratingNote={c.labels.ratingSourceNote} ratingNotePath="labels.ratingSourceNote" ratingValue={c.business.ratingValue} reviewCount={c.business.reviewCount} photoPath="brand.heroImage" photoValue={c.brand.heroImage} photoLabel="{photo: workshop exterior -sign, two cars in front}" />
      <Section bone style={{ padding: '0 56px' }}>
        <TrustStrip items={normalizeTrustStrip(c.enHomePage.trustStrip)} pathPrefix="enHomePage.trustStrip" />
      </Section>
      <Section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div><Eyebrow><EditableText path="enHomePage.servicesEyebrow" as="span">{c.enHomePage.servicesEyebrow}</EditableText></Eyebrow><EditableText path="enHomePage.servicesHeading" as="h2" style={{ ...T.h2, margin: '8px 0 0' }}>{c.enHomePage.servicesHeading}</EditableText></div>
          <BtnLink label={<EditableText path="enHomePage.servicesLinkLabel" as="span">{c.enHomePage.servicesLinkLabel}</EditableText>} href={servicesPathFor('en')} />
        </div>
        <div className="aza-grid aza-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
          {c.services.items.slice(0, 6).map((s) => {
            const index = c.services.items.indexOf(s);
            return <ServiceCard key={s.slug} icon={iconByName(s.icon)} title={s.nameEn} blurb={s.blurbEn} price={s.priceFrom} signal={s.signal} href={servicePathFor('en', s.slug)} c={c} titlePath={index >= 0 ? `services.items.${index}.nameEn` : undefined} blurbPath={index >= 0 ? `services.items.${index}.blurbEn` : undefined} pricePath={index >= 0 ? `services.items.${index}.priceFrom` : undefined} />;
          })}
        </div>
      </Section>
      <section style={{ background: aza.signal, color: '#FFF8F2' }}>
        <div className="aza-inner aza-grid" style={{ padding: '56px', display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 48, alignItems: 'center' }}>
          <div><Eyebrow color="rgba(255,255,255,0.85)"><EditableText path="enHomePage.rentalEyebrow" as="span">{c.enHomePage.rentalEyebrow}</EditableText></Eyebrow><EditableText path="enHomePage.rentalHeading" as="h2" style={{ ...T.h2, color: '#FFF', margin: '10px 0 12px' }}>{c.enHomePage.rentalHeading}</EditableText><EditableText path="enHomePage.rentalBody" as="p" multiline style={{ ...T.lead, color: 'rgba(255,255,255,0.9)', maxWidth: 620 }}>{c.enHomePage.rentalBody}</EditableText></div>
          <div><a href="/en/services/rental-car-damage" target="_top" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: 'rgba(0,0,0,0.18)', border: '1px solid rgba(255,255,255,0.35)', padding: '18px 22px', borderRadius: 10, color: '#FFF', ...T.h4, fontSize: 17 }}><EditableText path="enHomePage.rentalLinkLabel" as="span">{c.enHomePage.rentalLinkLabel}</EditableText> <Icon.arrow size={18} /></a></div>
        </div>
      </section>
      <Section bone>
        <Eyebrow><EditableText path="enHomePage.reviewsEyebrow" as="span">{c.enHomePage.reviewsEyebrow}</EditableText></Eyebrow><EditableText path="enHomePage.reviewsHeading" as="h2" style={{ ...T.h2, margin: '8px 0 22px' }}>{c.enHomePage.reviewsHeading}</EditableText>
        <div className="aza-grid aza-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
          {c.reviews.items.filter((r) => r.locale === 'en').slice(0, 3).map((r) => {
            const index = c.reviews.items.indexOf(r);
            return <ReviewCard key={`${r.name}-${index}`} r={r} pathPrefix={index >= 0 ? `reviews.items.${index}` : undefined} />;
          })}
        </div>
      </Section>
      <ContactTeaser c={c} lang="en" waHref={waHref} />
    </>
  );
}

function EnRentalDamage({ c, waHref }: { c: AutozonasaltasContent; waHref?: string }) {
  const steps = normalizeServiceSteps(c.rentalEn.steps);
  const dmg = normalizeDamageItems(c.rentalEn.commonDamage);
  const faqs = normalizeFaqItems(c.rentalEn.faqs);
  const checklist = normalizeTextItems(c.rentalPage.checklistItems);
  return (
    <>
      <section style={{ background: aza.teal900, color: aza.paper }}>
        <div className="aza-inner" style={{ padding: '48px 56px 56px' }}>
          <div style={{ ...T.small, color: 'rgba(255,255,255,0.55)', marginBottom: 14 }}>Home / Services / Rental car damage</div>
          <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: 56 }}>
            <div>
              <Badge tone="signal" style={{ marginBottom: 14 }}><EditableText path="rentalEn.badge" as="span">{c.rentalEn.badge}</EditableText></Badge>
              <EditableText path="rentalEn.title" as="h1" multiline style={{ ...T.h1, color: aza.paper, margin: 0 }}>{c.rentalEn.title}</EditableText>
              <EditableText path="rentalEn.subtitle" as="p" multiline style={{ ...T.lead, marginTop: 18, maxWidth: 580, color: 'rgba(255,255,255,0.85)' }}>{c.rentalEn.subtitle}</EditableText>
              <div style={{ display: 'flex', gap: 10, marginTop: 24, flexWrap: 'wrap' }}>
                <BtnWA size="lg" label={<EditableText path="rentalPage.waCtaLabel" as="span">{c.rentalPage.waCtaLabel}</EditableText>} href={waHref} />
                <BtnGhost size="lg" label={<EditableText path="rentalPage.howItWorksCtaLabel" as="span">{c.rentalPage.howItWorksCtaLabel}</EditableText>} href="#how" dark icon={<Icon.arrow size={16} />} />
              </div>
              <div style={{ display: 'flex', gap: 22, marginTop: 28, color: 'rgba(255,255,255,0.8)', ...T.small, flexWrap: 'wrap' }}>
                {checklist.map((item, idx) => <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon.check size={14} /><EditableText path={`rentalPage.checklistItems.${idx}.text`} as="span">{item.text}</EditableText></span>)}
              </div>
            </div>
            <PhotoSlot path="brand.bodyShopImage" value={c.brand.bodyShopImage} dark ratio="3/4" label="{photo: dented rental car next to an ocean lookout}" />
          </div>
        </div>
      </section>
      <Section bone>
        <Eyebrow><EditableText path="rentalPage.howEyebrow" as="span">{c.rentalPage.howEyebrow}</EditableText></Eyebrow><EditableText path="rentalPage.howHeading" as="h2" style={{ ...T.h2, margin: '8px 0 36px', maxWidth: 720 }}>{c.rentalPage.howHeading}</EditableText>
        <div className="aza-grid aza-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 0 }}>
          {steps.map((step, i) => (
            <div key={i} style={{ padding: '0 22px', borderLeft: i ? `1px solid ${aza.rule}` : 'none' }}>
              <div style={{ width: 36, height: 36, borderRadius: 999, background: aza.signal, color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', ...T.mono, fontWeight: 700 }}>{String(i + 1).padStart(2, '0')}</div>
              <EditableText path={`rentalEn.steps.${i}.title`} as="div" style={{ ...T.h4, marginTop: 14 }}>{step.title}</EditableText>
              {step.meta && <EditableText path={`rentalEn.steps.${i}.meta`} as="div" style={{ ...T.micro, color: aza.signal, marginTop: 4 }}>{step.meta}</EditableText>}
              <EditableText path={`rentalEn.steps.${i}.body`} as="div" multiline style={{ ...T.small, color: aza.smoke, marginTop: 10 }}>{step.body}</EditableText>
            </div>
          ))}
        </div>
      </Section>
      <Section>
        <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 56 }}>
          <div><Eyebrow><EditableText path="rentalPage.damageEyebrow" as="span">{c.rentalPage.damageEyebrow}</EditableText></Eyebrow><EditableText path="rentalPage.damageHeading" as="h2" style={{ ...T.h2, margin: '8px 0 14px' }}>{c.rentalPage.damageHeading}</EditableText><EditableText path="rentalPage.damageBody" as="p" multiline style={{ ...T.body, color: aza.smoke, maxWidth: 460 }}>{c.rentalPage.damageBody}</EditableText></div>
          <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {dmg.map((item, i) => (
              <div key={i} style={{ padding: 16, background: aza.paper, border: `1px solid ${aza.rule}`, borderRadius: 10, boxShadow: SHADOW.card }}>
                <EditableText path={`rentalEn.commonDamage.${i}.name`} as="div" style={{ ...T.body, fontWeight: 700, color: aza.ink }}>{item.name}</EditableText>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, ...T.small }}><span style={{ color: aza.smoke }}><EditableText path="rentalPage.damageCardPriceLabel" as="span">{c.rentalPage.damageCardPriceLabel}</EditableText></span><EditableText path={`rentalEn.commonDamage.${i}.price`} as="span" style={{ ...T.mono, fontWeight: 600 }}>{item.price}</EditableText></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, ...T.small }}><span style={{ color: aza.smoke }}><EditableText path="rentalPage.damageCardReadyLabel" as="span">{c.rentalPage.damageCardReadyLabel}</EditableText></span><EditableText path={`rentalEn.commonDamage.${i}.ready`} as="span" style={{ ...T.mono, fontWeight: 600 }}>{item.ready}</EditableText></div>
              </div>
            ))}
          </div>
        </div>
      </Section>
      <Section bone>
        <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 56 }}>
          <div><Eyebrow><EditableText path="rentalPage.faqEyebrow" as="span">{c.rentalPage.faqEyebrow}</EditableText></Eyebrow><EditableText path="rentalPage.faqHeading" as="h2" style={{ ...T.h2, margin: '8px 0 12px' }}>{c.rentalPage.faqHeading}</EditableText><BtnWA label={<EditableText path="rentalPage.faqCtaLabel" as="span">{c.rentalPage.faqCtaLabel}</EditableText>} href={waHref} /></div>
          <div>{faqs.map((faq, i) => <FAQItem key={i} q={faq.question} a={faq.answer} defaultOpen={i === 0} questionPath={`rentalEn.faqs.${i}.question`} answerPath={`rentalEn.faqs.${i}.answer`} />)}</div>
        </div>
      </Section>
      <section style={{ background: aza.signal, color: '#FFF' }}>
        <div className="aza-inner aza-grid" style={{ padding: '64px 56px', display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 56, alignItems: 'center' }}>
          <div><EditableText path="rentalPage.finalHeading" as="h2" style={{ ...T.h2, color: '#FFF', margin: 0 }}>{c.rentalPage.finalHeading}</EditableText><EditableText path="rentalPage.finalBody" as="p" multiline style={{ ...T.lead, color: 'rgba(255,255,255,0.9)', marginTop: 12, maxWidth: 560 }}>{c.rentalPage.finalBody}</EditableText></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}><BtnWA full size="lg" label={<EditableText path="labels.waCtaEn" as="span">{c.labels.waCtaEn}</EditableText>} href={waHref} /><BtnGhost label={<EditableText path="business.email" as="span" richText={false}>{c.business.email}</EditableText>} href={`mailto:${c.business.email}`} dark full /></div>
        </div>
      </section>
    </>
  );
}

function DeLanding({ c, waHref }: { c: AutozonasaltasContent; waHref?: string }) {
  const faqs = normalizeFaqItems(c.de.faqs);
  const services = normalizeTextItems(c.de.services);
  return (
    <>
      <Hero eyebrow={c.de.eyebrow} title={c.de.title} subtitle={c.de.subtitle} titlePath="de.title" subtitlePath="de.subtitle" eyebrowPath="de.eyebrow" ctaLabel={<EditableText path="labels.waCtaDe" as="span">{c.labels.waCtaDe}</EditableText>} waHref={waHref} secondaryLabel={<EditableText path="labels.pricingCtaDe" as="span">{c.labels.pricingCtaDe}</EditableText>} secondaryHref={quoteCtaPathFor('de')} ratingNote={c.labels.ratingSourceNote} ratingNotePath="labels.ratingSourceNote" ratingValue={c.business.ratingValue} reviewCount={c.business.reviewCount} photoPath="brand.heroImage" photoValue={c.brand.heroImage} photoLabel="{photo: workshop interior -bays clean}" />
      <Section>
        <div className="aza-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48 }}>
          <div>
            <Eyebrow><EditableText path="dePage.servicesEyebrow" as="span">{c.dePage.servicesEyebrow}</EditableText></Eyebrow><EditableText path="dePage.servicesHeading" as="h2" style={{ ...T.h2, margin: '8px 0 16px' }}>{c.dePage.servicesHeading}</EditableText>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {services.map((item, i) => <li key={`${item.text}-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'center', ...T.body, fontSize: 15 }}><Icon.check size={15} /> <EditableText path={`de.services.${i}.text`} as="span">{item.text}</EditableText></li>)}
            </ul>
          </div>
          <div><Eyebrow><EditableText path="dePage.faqEyebrow" as="span">{c.dePage.faqEyebrow}</EditableText></Eyebrow>{faqs.map((faq, i) => <FAQItem key={i} q={faq.question} a={faq.answer} defaultOpen={i === 0} questionPath={`de.faqs.${i}.question`} answerPath={`de.faqs.${i}.answer`} />)}</div>
        </div>
      </Section>
      <section style={{ background: aza.teal900, color: aza.paper }}>
        <div className="aza-inner aza-grid" style={{ padding: '48px 56px', display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 56, alignItems: 'center' }}>
          <div><EditableText path="dePage.ctaHeading" as="h2" style={{ ...T.h2, color: aza.paper, margin: 0 }}>{c.dePage.ctaHeading}</EditableText><EditableText path="dePage.ctaBody" as="p" multiline style={{ ...T.lead, color: 'rgba(255,255,255,0.8)', marginTop: 10, maxWidth: 540 }}>{c.dePage.ctaBody}</EditableText></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}><BtnWA full size="lg" label={<EditableText path="dePage.waCtaLabel" as="span">{c.dePage.waCtaLabel}</EditableText>} href={waHref} /><BtnGhost label={<EditableText path="business.email" as="span" richText={false}>{c.business.email}</EditableText>} href={`mailto:${c.business.email}`} dark full /></div>
        </div>
      </section>
    </>
  );
}

/* ─── SEO / JSON-LD head injection ────────────────────────────────── */
function useSeo(c: AutozonasaltasContent, lang: Lang, routePath: string, domain: string) {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const origin = `https://${domain}`;
    const title = c.seo.defaultTitle;
    const prevTitle = document.title;
    document.title = title;
    document.documentElement.lang = lang === 'pt' ? 'pt-PT' : lang;

    const nodes: HTMLElement[] = [];
    const add = (el: HTMLElement) => { document.head.appendChild(el); nodes.push(el); };
    const meta = (name: string, content: string, prop = false) => {
      const m = document.createElement('meta');
      m.setAttribute(prop ? 'property' : 'name', name);
      m.content = content; add(m);
    };
    meta('description', c.seo.defaultDescription);
    meta('og:title', title, true);
    meta('og:description', c.seo.defaultDescription, true);
    meta('og:type', 'website', true);
    meta('og:url', origin + routePath, true);
    meta('og:locale', lang === 'pt' ? 'pt_PT' : lang === 'de' ? 'de_DE' : 'en_GB', true);
    meta('twitter:card', 'summary_large_image');
    const link = (rel: string, href: string, hreflang?: string) => {
      const l = document.createElement('link');
      l.rel = rel; l.href = href; if (hreflang) l.hreflang = hreflang; add(l);
    };
    link('canonical', origin + routePath);
    link('alternate', origin + '/', 'pt-PT');
    link('alternate', origin + '/en/', 'en');
    link('alternate', origin + '/de/', 'de');
    link('alternate', origin + '/', 'x-default');

    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': ['AutoRepair', 'LocalBusiness'],
      '@id': `${origin}/#org`,
      name: c.business.name,
      legalName: c.business.legalName,
      url: origin,
      telephone: c.business.phone,
      email: c.business.email,
      priceRange: '€€',
      address: {
        '@type': 'PostalAddress',
        streetAddress: c.business.street,
        addressLocality: `${c.business.locality}`,
        addressRegion: c.business.region,
        postalCode: c.business.postalCode,
        addressCountry: 'PT',
      },
      geo: c.business.geoLat ? { '@type': 'GeoCoordinates', latitude: c.business.geoLat, longitude: c.business.geoLng } : undefined,
      areaServed: lines(c.seo.serviceAreas.replace(/,/g, '\n')),
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '08:30', closes: '18:30' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday', opens: '09:00', closes: '13:00' },
      ],
      paymentAccepted: paymentAcceptedValue(c.business.payments),
      currenciesAccepted: 'EUR',
      aggregateRating: { '@type': 'AggregateRating', ratingValue: c.business.ratingValue.replace(',', '.'), reviewCount: String(c.business.reviewCount) },
    });
    add(ld);

    return () => { document.title = prevTitle; nodes.forEach((n) => n.remove()); };
  }, [c, lang, routePath, domain]);
}

/* ─── Responsive stylesheet ───────────────────────────────────────── */
const STYLE = `
.aza-inner{max-width:1240px;margin:0 auto}
.aza-sticky-cta{display:none}
@media(max-width:920px){
  .aza-grid,.aza-grid-3,.aza-grid-4{grid-template-columns:1fr !important}
  .aza-inner{padding-left:18px !important;padding-right:18px !important}
  .aza-header{padding-left:18px !important;padding-right:18px !important}
  .aza-nav,.aza-hide-mobile{display:none !important}
  .aza-sticky-cta{display:grid !important}
}
@media(min-width:921px) and (max-width:1180px){
  .aza-grid-4{grid-template-columns:repeat(2,1fr) !important}
}
`;

/* ─── Router (default export) ─────────────────────────────────────── */
export default function AutozonasaltasRenderer({ content, routePath, domain, publicWriteToken }: SiteRendererProps) {
  const c = content as unknown as AutozonasaltasContent;
  const path = (routePath || '/').replace(/\/+$/, '') || '/';
  const lang: Lang = path === '/de' || path.startsWith('/de/') ? 'de' : path === '/en' || path.startsWith('/en/') || path.startsWith('/en') ? 'en' : 'pt';

  useSeo(c, lang, routePath || '/', domain || 'autozonasaltas.pt');

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!document.getElementById('aza-fonts')) {
      const l = document.createElement('link');
      l.id = 'aza-fonts'; l.rel = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Archivo:wght@500;600;700;800&family=Manrope:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap';
      document.head.appendChild(l);
    }
    trackEvent('page_view', { path: routePath || '/' });
  }, [routePath]);

  const waHref = waUrl(c.business.whatsapp, 'Olá Auto Zonas Altas, gostaria de marcar a minha viatura.');
  const tel = sanitizePhoneHref(c.business.phone);

  let page: ReactNode;
  let current = path;
  if (path === '/de') { page = <DeLanding c={c} waHref={waHref} />; current = '/de/'; }
  else if (path === '/de/preise') { page = <ContactPage c={c} lang="de" waHref={waHref} />; current = quoteCtaPathFor('de'); }
  else if (path === '/de/kontakt') { page = <ContactPage c={c} lang="de" waHref={waHref} />; }
  else if (path.startsWith('/de/')) { page = <DeLanding c={c} waHref={waHref} />; current = '/de/'; }
  else if (path === '/en') { page = <EnHome c={c} waHref={waHref} />; current = '/en/'; }
  else if (path === '/en/services') { page = <ServicesIndex c={c} lang="en" waHref={waHref} />; }
  else if (path === '/en/pricing') { page = <ContactPage c={c} lang="en" waHref={waHref} />; current = quoteCtaPathFor('en'); }
  else if (path === '/en/services/rental-car-damage') { page = <EnRentalDamage c={c} waHref={waHref} />; }
  else if (path === '/en/services/body-shop') { page = <ServicePage slug="bate-chapa" c={c} lang="en" waHref={waHref} />; }
  else if (path === '/en/services/mechanic') { page = <ServicePage slug="mecanica" c={c} lang="en" waHref={waHref} />; }
  else if (path.startsWith('/en/services/')) { page = <ServicePage slug={path.slice('/en/services/'.length)} c={c} lang="en" waHref={waHref} />; }
  else if (path === '/en/contact') { page = <ContactPage c={c} lang="en" waHref={waHref} />; }
  else if (path === '/sobre') { page = <AboutPage c={c} waHref={waHref} />; }
  else if (path === '/servicos') { page = <ServicesIndex c={c} lang="pt" waHref={waHref} />; }
  else if (path.startsWith('/servicos/')) { page = <ServicePage slug={path.slice('/servicos/'.length)} c={c} lang="pt" waHref={waHref} />; }
  else if (path === '/precos') { page = <BookingPage c={c} lang={lang} waHref={waHref} publicWriteToken={publicWriteToken} />; current = quoteCtaPathFor('pt'); }
  else if (path === '/marcacao') { page = <BookingPage c={c} lang="pt" waHref={waHref} publicWriteToken={publicWriteToken} />; }
  else if (path === '/contactos') { page = <ContactPage c={c} lang="pt" waHref={waHref} />; }
  else if (path === '/testemunhos') { page = <ReviewsPage c={c} />; }
  else if (path === '/blog') { page = <BlogIndex c={c} />; }
  else if (path.startsWith('/blog/')) { page = <BlogPost c={c} waHref={waHref} />; }
  else { page = <HomePT c={c} waHref={waHref} />; current = '/'; }

  return (
    <div style={{ background: aza.paper, color: aza.ink, fontFamily: FONT_BODY }}>
      <style dangerouslySetInnerHTML={{ __html: STYLE }} />
      <SiteHeader lang={lang} current={current} c={c} waHref={waHref} />
      <main>{page}</main>
      <SiteFooter lang={lang} c={c} />
      <StickyMobileCTAs lang={lang} waHref={waHref} tel={tel} c={c} />
    </div>
  );
}
